// src/services/apiClient.js - Extended with location management
import axios from 'axios';
import { API_ENDPOINTS } from '@caro/shared/constants';
import { kbMockApi } from './kb/kbMockApi';
import * as indexedDB from '../utils/indexedDB';
import * as tokenEncryption from '../utils/tokenEncryption';
import { handleSessionExpiry } from '../utils/sessionRecovery';
import { offlineQueue } from '@caro/shared/services';
import requestTracker from './requestTracker';

// Get base URL from environment or use default
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.caro.care';

// Create axios instance with default config
const axiosInstance = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  timeout: 3600000, // 1 hour timeout for large file uploads (up to 20GB)
  withCredentials: true,
});

// Create a public axios instance WITHOUT auth interceptors
// Used for endpoints that don't require authentication (e.g., public alarm access)
const publicAxiosInstance = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  timeout: 30000,
  withCredentials: false,
});

// Track if we're currently refreshing to avoid multiple refresh requests
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

// Helper function to get token from storage
const getTokenFromStorage = async (key) => {
  try {
    // Try IndexedDB first
    if (indexedDB.isAvailable()) {
      const storedValue = await indexedDB.getItem(key);

      if (storedValue) {
        // Try decryption first
        try {
          const decryptedToken = await tokenEncryption.decryptToken(storedValue);
          if (decryptedToken) {
            return decryptedToken;
          }
        } catch (decryptError) {
          // Decryption failed — the value might be a raw JWT (unencrypted)
          // or the crypto key was invalidated. Check if it looks like a JWT.
          if (typeof storedValue === 'string' && storedValue.startsWith('eyJ')) {
            console.warn('[apiClient] Decryption failed but value looks like a raw JWT, using it directly');
            return storedValue;
          }
          console.warn('[apiClient] Decryption failed for', key, '— falling back to localStorage');
        }
      }
    }

    // Fallback to localStorage
    const localToken = localStorage.getItem(key);
    if (localToken) {
      return localToken;
    }

    return null;
  } catch (error) {
    console.warn('[apiClient] Error retrieving token:', error?.message);
    // Last resort
    return localStorage.getItem(key);
  }
};

// Helper function to set token in storage
const setTokenInStorage = async (key, token) => {
  if (indexedDB.isAvailable()) {
    const encryptedToken = await tokenEncryption.encryptToken(token);
    await indexedDB.setItem(key, encryptedToken);
  } else {
    localStorage.setItem(key, token);
  }
};

// Add request interceptor to include auth token
axiosInstance.interceptors.request.use(
  async (config) => {
    let token = await getTokenFromStorage('auth_token');

    // Retry once if token retrieval failed (IndexedDB can be slow/busy)
    if (!token) {
      await new Promise(r => setTimeout(r, 100));
      token = await getTokenFromStorage('auth_token');
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else if (process.env.NODE_ENV === 'development') {
      console.warn('[apiClient] No auth token found for request:', config.url);
    }

    // Page-level progress bar hook. Skip noisy background polls via the
    // `__silent: true` axios flag so badge/presence pings don't flash
    // the bar every few seconds.
    if (!config.__silent) {
      requestTracker.start();
      config.__trackedByProgress = true;
    }

    return config;
  },
  (error) => {
    if (process.env.NODE_ENV === 'development') {
      console.error('Request interceptor error:', error);
    }
    return Promise.reject(error);
  }
);

// Add response interceptor to handle common errors and token refresh
// Wire the offline queue to this axios instance so replayed requests use
// the same auth/refresh/interceptor chain.
offlineQueue.bindClient(axiosInstance);

axiosInstance.interceptors.response.use(
  (response) => {
    if (response.config?.__trackedByProgress) requestTracker.end();
    return response;
  },
  async (error) => {
    if (error.config?.__trackedByProgress) requestTracker.end();
    const originalRequest = error.config;

    // Offline-queue integration: if the request failed because the browser
    // was offline (no response received) AND it's a write method, stash it
    // for replay when the connection returns. Reads are left to the SW
    // cache strategy. Skip anything already being replayed.
    const isNetworkFailure = !error.response && error.code !== 'ECONNABORTED';
    const method = String(originalRequest?.method || '').toLowerCase();
    const isWrite = method === 'post' || method === 'put' || method === 'patch' || method === 'delete';
    const isReplay = originalRequest?.__offlineReplay === true;
    const isNoQueue = originalRequest?.__noQueue === true;
    if (isNetworkFailure && isWrite && !isReplay && !isNoQueue && typeof navigator !== 'undefined' && !navigator.onLine) {
      try {
        offlineQueue.enqueue({
          method,
          url: originalRequest.url,
          data: originalRequest.data,
          config: { headers: originalRequest.headers, params: originalRequest.params },
          description: `${method.toUpperCase()} ${originalRequest.url}`,
        });
      } catch {
        /* swallow enqueue errors — better to surface the network error */
      }
    }

    // Handle authentication errors
    if (error.response && error.response.status === 401) {
      // Don't try to refresh on these endpoints
      const noRefreshPaths = ['/auth/refresh', '/auth/login', '/auth/microsoft'];
      const isNoRefreshPath = noRefreshPaths.some(path => originalRequest.url?.includes(path));

      // Try to refresh the token if we haven't already tried
      if (!originalRequest._retry && !isNoRefreshPath) {
        if (isRefreshing) {
          // If already refreshing, queue this request
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then(token => {
              originalRequest.headers['Authorization'] = 'Bearer ' + token;
              return axiosInstance(originalRequest);
            })
            .catch(err => {
              return Promise.reject(err);
            });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          // Get refresh token from storage
          const refreshToken = await getTokenFromStorage('refresh_token');

          if (refreshToken) {
            // Attempt to refresh the token
            const response = await axios.post(`${API_BASE_URL}/api/auth/refresh`, {
              refreshToken
            });

            if (response.data.success && response.data.token) {
              const newAccessToken = response.data.token;
              const newRefreshToken = response.data.refreshToken || refreshToken;

              // Check if impersonation expired during refresh
              if (response.data.impersonationExpired) {
                sessionStorage.removeItem('caro_impersonation');
                window.dispatchEvent(new CustomEvent('impersonation-expired'));
              }

              // Store new tokens
              await setTokenInStorage('auth_token', newAccessToken);
              await setTokenInStorage('refresh_token', newRefreshToken);

              // Update the failed request with new token
              originalRequest.headers['Authorization'] = 'Bearer ' + newAccessToken;

              processQueue(null, newAccessToken);
              isRefreshing = false;

              // Retry the original request
              return axiosInstance(originalRequest);
            }
          }
        } catch (refreshError) {
          processQueue(refreshError, null);
          isRefreshing = false;

          if (process.env.NODE_ENV === 'development') {
            console.error('Token refresh failed:', refreshError);
          }

          // Use automatic session recovery
          await handleSessionExpiry('token_refresh_failed');

          return Promise.reject(refreshError);
        }
      }

      // No refresh token or refresh already attempted
      // Only trigger session expiry if the original request actually had an auth header
      // (if it didn't, the token retrieval failed — don't log the user out for a transient error)
      if (originalRequest.headers?.Authorization) {
        await handleSessionExpiry('session_expired');
      } else {
        console.warn('[apiClient] 401 on request without auth header — skipping session expiry (likely token retrieval failure)');
      }
    }
    return Promise.reject(error);
  }
);

// API client for authentication
export const authApi = {
  // Standard authentication methods
  login: (credentials) => axiosInstance.post('/auth/login', credentials),
  register: (userData) => axiosInstance.post('/auth/register', userData),
  getCurrentUser: () => axiosInstance.get('/auth/me'),
  changePassword: (passwordData) => axiosInstance.put('/auth/change-password', passwordData),
  logout: (refreshToken) => axiosInstance.post('/auth/logout', { refreshToken }),
  sessionCheck: () => axiosInstance.get('/auth/me', { timeout: 5000 }),

  // Refresh token
  refreshToken: (refreshToken) => axiosInstance.post('/auth/refresh', { refreshToken }),

  // Impersonation
  startImpersonation: (targetUserId) => axiosInstance.post('/auth/impersonate', { targetUserId }),
  stopImpersonation: () => axiosInstance.post('/auth/impersonate/stop'),
  getImpersonationStatus: () => axiosInstance.get('/auth/impersonate/status'),

  // Get user devices
  getDevices: () => axiosInstance.get('/auth/devices'),

  // Revoke device
  revokeDevice: (fingerprint) => axiosInstance.delete(`/auth/devices/${fingerprint}`),
  
  // Microsoft authentication method
  microsoftLogin: (redirectTo = '/dashboard') => {
    // The frontend URL (localhost in development, actual domain in production)
    const frontendUrl = process.env.REACT_APP_FRONTEND_URL || 'https://portal.caro.care';
    
    // Add a callbackUrl parameter to tell the API where to redirect after auth
    const callbackUrl = encodeURIComponent(`${frontendUrl}/auth-callback`);
    
    // Redirect to the backend Microsoft authentication endpoint
    const authUrl = `${API_BASE_URL}/api/auth/microsoft?callbackUrl=${callbackUrl}&redirectTo=${encodeURIComponent(redirectTo)}`;
    window.location.href = authUrl;
    return Promise.resolve({ success: true });
  },
  
  // Handle authentication callback with token
  handleAuthCallback: async (token) => {
    if (!token) {
      return { success: false, message: 'No token provided' };
    }
    
    try {
      // Store the token
      localStorage.setItem('auth_token', token);
      
      // Fetch the user data
      const response = await axiosInstance.get('/auth/me');
      
      if (response.data.success && response.data.user) {
        return { 
          success: true, 
          user: response.data.user 
        };
      } else {
        throw new Error('Invalid user data received');
      }
    } catch (err) {
      // Clean up if there's an error
      localStorage.removeItem('auth_token');
      return { 
        success: false, 
        message: err.response?.data?.message || err.message || 'Authentication failed' 
      };
    }
  }
};

// API client for tickets
export const ticketsApi = {
  getAll: (params) => axiosInstance.get('/tickets', { params }),
  getById: (id) => axiosInstance.get(`/tickets/${id}`),
  create: (ticketData) => axiosInstance.post('/tickets', ticketData),
  update: (id, ticketData) => axiosInstance.put(`/tickets/${id}`, ticketData),
  delete: (id) => axiosInstance.delete(`/tickets/${id}`),
  addComment: (id, commentData) => axiosInstance.post(`/tickets/${id}/messages`, commentData),
  addAttachment: (id, formData) => axiosInstance.post(`/tickets/${id}/attachments`, formData),
  deleteAttachment: (ticketId, attachmentId) => axiosInstance.delete(`/tickets/${ticketId}/attachments/${attachmentId}`),
  // `assignTicket` (POST /tickets/:id/assign) was removed: no route exists on the API and
  // nothing in the portal called it (spec 20260930-leveranciers F-4). Supplier assignment
  // lives in suppliersApi.
  // Through PUT /tickets/:id (the state machine validates the transition). The former
  // POST /tickets/:id/status never existed on the API, so every bulk close failed.
  changeStatus: (id, status) => axiosInstance.put(`/tickets/${id}`, { status }),
  getStatistics: () => axiosInstance.get('/tickets/statistics'),

  // Follow/Unfollow tickets
  followTicket: (id) => axiosInstance.post(`/tickets/${id}/follow`),
  follow: (id) => axiosInstance.post(`/tickets/${id}/follow`),
  unfollowTicket: (id) => axiosInstance.delete(`/tickets/${id}/follow`),
  unfollow: (id) => axiosInstance.delete(`/tickets/${id}/follow`),
  getFollowers: (id) => axiosInstance.get(`/tickets/${id}/followers`),

  // Known issues and solutions
  createKnownIssue: (issueData) => axiosInstance.post('/known-issues', issueData),
  getKnownIssues: (params) => axiosInstance.get('/known-issues', { params }),
  getKnownIssueById: (id) => axiosInstance.get(`/known-issues/${id}`),
  updateKnownIssue: (id, issueData) => axiosInstance.put(`/known-issues/${id}`, issueData),
  deleteKnownIssue: (id) => axiosInstance.delete(`/known-issues/${id}`),

  createSolution: (solutionData) => axiosInstance.post('/solutions', solutionData),
  getSolutions: (params) => axiosInstance.get('/solutions', { params }),
  getSolutionById: (id) => axiosInstance.get(`/solutions/${id}`),
  updateSolution: (id, solutionData) => axiosInstance.put(`/solutions/${id}`, solutionData),
  deleteSolution: (id) => axiosInstance.delete(`/solutions/${id}`),

  // Apply solution to a ticket
  applySolution: (id, data) => axiosInstance.post(`/tickets/${id}/solutions`, data),

  // Get interventions linked to a ticket. Symmetric read path (spec 20260701, FR-3.2);
  // falls back to the field-interventions query for older backends.
  getTicketInterventions: (ticketId) => axiosInstance.get(`/tickets/${ticketId}/interventions`),
  getInterventions: (ticketId) => axiosInstance.get('/field-interventions', { params: { ticket_id: ticketId } }),

  // Link an existing intervention to a ticket / unlink it (spec 20260701, FR-3.3)
  linkIntervention: (ticketId, interventionId) => axiosInstance.post(`/tickets/${ticketId}/interventions/link`, { interventionId }),
  unlinkIntervention: (ticketId, interventionId) => axiosInstance.delete(`/tickets/${ticketId}/interventions/${interventionId}/link`),

  // Request physical intervention
  requestPhysicalIntervention: (id, data) => axiosInstance.post(`/tickets/${id}/request-intervention`, data),
  requestIntervention: (id, data) => axiosInstance.post(`/tickets/${id}/request-intervention`, data),

  // Reschedule intervention
  rescheduleIntervention: (ticketId, interventionId, data) => axiosInstance.put(`/tickets/${ticketId}/interventions/${interventionId}/reschedule`, data),

  // Cancel intervention
  cancelIntervention: (ticketId, interventionId, data) => axiosInstance.put(`/tickets/${ticketId}/interventions/${interventionId}/cancel`, data),

  // Get tickets by location
  getByLocation: (locationId) => axiosInstance.get(`/tickets`, { params: { locationId } }),

  // Inbound email quarantine review (spec 20260625-ticket-email-reply, T030/T031)
  getQuarantine: (params) => axiosInstance.get('/inbound/quarantine', { params }),
  attachQuarantine: (id, data) => axiosInstance.post(`/inbound/quarantine/${id}/attach`, data),
  dismissQuarantine: (id, data) => axiosInstance.post(`/inbound/quarantine/${id}/dismiss`, data),
};

// API client for locations
export const locationsApi = {
  // Get all locations with optional filters
  // ONE page, at most 500 rows — the API caps `limit` at 500 (utils/locationsListCache.js)
  // whatever a caller asks for, and sorts on created_at DESC. Any screen that needs the
  // whole list must use getAllPages() below; reading a single page here is how the EnOcean
  // import stopped matching the units of every older site once the tenant passed 500.
  getAll: (params) => axiosInstance.get('/locations', {
    params: { limit: 500, ...params }
  }),

  // EVERY location matching `params`, walked page by page and merged into the same
  // `{ data: { success, data, pagination } }` shape as getAll(), so a caller can switch
  // without touching how it reads the response. Pages after the first are fetched in
  // small parallel batches; the API dedups in-flight requests and caches for 30s.
  //
  // Capped at MAX_PAGES so a runaway total can never hang a screen. The cap is loud.
  getAllPages: async (params = {}) => {
    const PAGE_SIZE = 500;
    const MAX_PAGES = 40;
    const BATCH = 4;
    const base = { ...params, limit: PAGE_SIZE };
    const first = await axiosInstance.get('/locations', { params: { ...base, page: 1 } });
    let all = first.data?.data || [];
    const pagination = first.data?.pagination || {};
    const totalPages = Math.min(pagination.pages || 1, MAX_PAGES);
    for (let start = 2; start <= totalPages; start += BATCH) {
      const end = Math.min(start + BATCH - 1, totalPages);
      const pages = [];
      for (let page = start; page <= end; page++) {
        pages.push(axiosInstance.get('/locations', { params: { ...base, page } }));
      }
      const responses = await Promise.all(pages);
      for (const r of responses) all = all.concat(r.data?.data || []);
    }
    if ((pagination.pages || 1) > MAX_PAGES) {
      console.warn(
        `[locationsApi.getAllPages] Truncated at ${MAX_PAGES} pages (${pagination.pages} available). `
        + 'The location list is incomplete.'
      );
    }
    return {
      ...first,
      data: {
        ...first.data,
        data: all,
        pagination: { ...pagination, page: 1, limit: all.length, pages: 1 },
      },
    };
  },

  // Get specific location by ID
  getById: (id) => axiosInstance.get(`/locations/${id}`),

  // Get locations by type (site, building, floor, unit, room, etc.)
  //
  // ⚠ THERE IS NO SUCH ROUTE. `/api/locations` mounts routes/dynamic/locations.js, which
  // has no `/type/:type` handler, so this 404s. Every caller that swallowed the error
  // rendered an empty dropdown and looked like "there are no sites". Use getSites() for
  // sites; nothing else currently needs a by-type lookup. Kept only so an existing caller
  // fails visibly rather than being silently deleted from under it.
  getByType: (type) => axiosInstance.get(`/locations/type/${type}`),

  // Sites the CURRENT user may see. Admins get every site in the system — which is the
  // behaviour cross-organisation screens rely on — and everyone else gets the sites they
  // hold LocationAccess for. Response shape is `{ success, sites: [...] }`, NOT the usual
  // `{ data }`, and each site carries `companyId`, so a cross-org screen can narrow the
  // list itself instead of asking for a server-side filter that does not exist.
  getSites: () => axiosInstance.get('/locations/sites'),

  // Phone-number verification page (/location-phone-numbers). One tenant-scoped call
  // returns every unit with data.phoneNumber next to its SIP extension and Clearvox
  // number, plus the site list for the filter; `siteId` narrows it server-side.
  getPhoneNumberOverview: (params) => axiosInstance.get('/locations/phone-numbers', { params }),
  // Writes data.phoneNumber in the canonical object shape; null/'' clears it.
  setPhoneNumber: (id, phoneNumber) => axiosInstance.put(`/locations/${id}/phone-number`, { phoneNumber }),
  // Writes data.emergencyIntegrationIdentifier (the UMO code the dispatch flow hands
  // the nursing service) the same way: server-side merge, audited, null/'' clears it.
  // Opened by locations.phone_numbers OR unit_audit.manage.
  setEmergencyIdentifier: (id, emergencyIntegrationIdentifier) =>
    axiosInstance.put(`/locations/${id}/emergency-identifier`, { emergencyIntegrationIdentifier }),
  // Bulk: data.phoneNumber := 31121 + SIP extension for the named units. The server
  // recomputes the proposal; only the ids travel. Nothing happens without a click.
  applyProposedPhoneNumbers: (locationIds) =>
    axiosInstance.post('/locations/phone-numbers/apply-proposed', { locationIds }),

  // Het standaard-startscherm van een site. Lezen mag met locations.view; bewaren
  // vraagt locations.edit — dit is een locatie-instelling, hij woont in app_config
  // naast de featurevlaggen.
  getHomeLayoutDefault: (id) => axiosInstance.get(`/locations/${id}/home-layout`),
  putHomeLayoutDefault: (id, data) => axiosInstance.put(`/locations/${id}/home-layout`, data),

  // Get locations by parent ID (children of a specific location)
  getByParent: (parentId) => axiosInstance.get(`/locations/${parentId}/children`),

  // Get full location hierarchy (returns complete tree structure)
  getHierarchy: () => axiosInstance.get('/locations/tree'),

  // Create new location
  create: (locationData) => axiosInstance.post('/locations', locationData),

  // Update existing location
  update: (id, locationData) => axiosInstance.put(`/locations/${id}`, locationData),

  // Delete location
  delete: (id) => axiosInstance.delete(`/locations/${id}`),

  // Hard-refresh all resident tablets at a location (push new app version)
  refreshTablets: (id) => axiosInstance.post(`/locations/${id}/refresh-tablets`),

  // Get devices connected to a location
  getDevices: (locationId) => axiosInstance.get(`/locations/${locationId}/devices`),

  // Get user's accessible locations
  getUserAccessibleLocations: (locationType, parentId = null) => {
    const params = {};
    if (parentId) params.parentId = parentId;
    return axiosInstance.get(`/locations/accessible/${locationType}`, { params });
  },

  // Get statistics about locations
  getStatistics: () => axiosInstance.get('/locations/statistics'),

  // Get location ancestors (parent chain up to root)
  getAncestors: (locationId) => axiosInstance.get(`/locations/${locationId}/ancestors`),

  // Search locations by name or properties
  search: (query) => axiosInstance.get('/locations/search', { params: { query } }),

  // Manage location access
  grantAccess: (locationId, userId) => axiosInstance.post(`/locations/${locationId}/access`, { userId }),
  revokeAccess: (locationId, userId) => axiosInstance.delete(`/locations/${locationId}/access/${userId}`),

  // `getSites` stond hier een TWEEDE keer, identiek. In JavaScript wint de laatste
  // definitie stil, dus de uitleg hierboven hoorde bij een sleutel die overschreven werd —
  // esbuild waarschuwde er bij elke build over. Eén definitie, hierboven, mét de reden.

  // Get all location IDs within a site (for filtering)
  getSiteLocationIds: (siteId) => axiosInstance.get(`/locations/sites/${siteId}/location-ids`),
};

// API client for location types
export const locationTypesApi = {
  // Get all location types
  getAll: () => axiosInstance.get('/locationtypes'),

  // Get location type by ID
  getById: (id) => axiosInstance.get(`/locationtypes/${id}`),
};

// API client for devices
export const devicesApi = {
  // Get all devices
  getAll: (params) => axiosInstance.get('/devices', {
    params: { limit: 500, ...params }
  }),

  // Get device by ID
  getById: (id) => axiosInstance.get(`/devices/${id}`),

  // Get devices by location
  getByLocation: (locationId) => axiosInstance.get(`/devices/location/${locationId}`, {
    params: { limit: 500 }
  }),

  // EVERY device under a location, walked page by page and merged into the same
  // `{ data: { success, data, meta } }` shape as getByLocation(). The single-page call
  // above stops at 500: a site of 90 apartments at six devices each is past that, and
  // the unit audit then reported the apartments on the missing page as empty.
  getAllByLocation: async (locationId) => {
    const PAGE_SIZE = 500;
    const MAX_PAGES = 20;
    const url = `/devices/location/${locationId}`;
    const first = await axiosInstance.get(url, { params: { limit: PAGE_SIZE, page: 1 } });
    let all = first.data?.data || [];
    const totalPages = first.data?.meta?.totalPages || 1;
    for (let page = 2; page <= Math.min(totalPages, MAX_PAGES); page++) {
      const r = await axiosInstance.get(url, { params: { limit: PAGE_SIZE, page } });
      all = all.concat(r.data?.data || []);
    }
    if (totalPages > MAX_PAGES) {
      console.warn(`[devicesApi.getAllByLocation] Truncated at ${MAX_PAGES} pages (${totalPages} available).`);
    }
    return { ...first, data: { ...first.data, data: all } };
  },

  // Create new device
  create: (deviceData) => axiosInstance.post('/devices', deviceData),

  // Update existing device
  update: (id, deviceData) => axiosInstance.put(`/devices/${id}`, deviceData),

  // Delete device — permanent. Prefer archive() for anything that ever ran.
  delete: (id) => axiosInstance.delete(`/devices/${id}`),

  // Archive instead of delete (CARO-internal only on the API). The device keeps
  // its history and lineage but leaves lists, counts, the unit audit and
  // alarming; its MAC/UUID move to the archive so a replacement can take over.
  getArchive: (id) => axiosInstance.get(`/devices/${id}/archive`),
  archive: (id, { reason, replacedByDeviceId = null }) =>
    axiosInstance.post(`/devices/${id}/archive`, { reason, replacedByDeviceId }),
  unarchive: (id) => axiosInstance.post(`/devices/${id}/unarchive`),
  setSuccessor: (id, replacedByDeviceId) =>
    axiosInstance.put(`/devices/${id}/successor`, { replacedByDeviceId }),

  // Ping single device to check if it's working (ICMP only — see probe())
  ping: (id) => axiosInstance.post(`/devices/${id}/ping`),

  // Live probe: asks the device NOW via whatever mechanism reaches it —
  // Hexnode MDM (Bode tablets), an MQTT ping/pong (caro-agent devices) or
  // ICMP. Returns { method, reachable, latencyMs, lastSeen, detail }.
  // `reachable === null` means the device cannot be asked at all (passive
  // EnOcean button, or the transport itself was unavailable).
  probe: (id) => axiosInstance.post(`/devices/${id}/probe`),

  // Get device status and history
  getStatus: (id) => axiosInstance.get(`/devices/${id}/status`),

  // Ping multiple devices at once
  pingBatch: (deviceIds) => axiosInstance.post('/devices/ping/batch', { deviceIds }),

  // Get ping history for a device
  getPingHistory: (id, params) => {
    return axiosInstance.get(`/devices/${id}/ping-history`, { params });
  },

  // Get ping statistics for charts
  getPingStats: (id, params) => {
    return axiosInstance.get(`/devices/${id}/ping-stats`, { params });
  },

  // Restart device
  restart: (id) => axiosInstance.post(`/devices/${id}/restart`),

  // Update device firmware
  updateFirmware: (id) => axiosInstance.post(`/devices/${id}/update-firmware`),

  // Get device logs
  getLogs: (id, params) => axiosInstance.get(`/devices/${id}/logs`, { params }),

  // Search devices
  search: (query) => axiosInstance.get('/devices/search', { params: { query } }),

  // SSH connection endpoint
  getSSHConfig: (id) => axiosInstance.get(`/devices/${id}/ssh-config`),

  // Test SSH connectivity
  testSSH: (id, params) => axiosInstance.post(`/devices/${id}/test-ssh`, params),

  // Get device types
  getDeviceTypes: (params) => axiosInstance.get('/devicetypes', { params }),

  // Uninstall a service from a device
  uninstallService: (deviceId, data) => axiosInstance.post(`/devices/${deviceId}/services/uninstall`, data),

  // Herinstalleer een service ZONDER hem eerst te verwijderen. `update.sh` is de
  // volledige installer, dus dit repareert een kapotte of verdwenen installatie.
  // De server toetst de leverbaarheid vooraf en antwoordt 409 als het pakket
  // ontbreekt — er gaat dan niets naar het toestel.
  reinstallService: (deviceId, data) => axiosInstance.post(`/devices/${deviceId}/services/reinstall`, data),

  // Restart a single service on a device (no reboot) — agent v25 'restartService'
  restartService: (deviceId, data) => axiosInstance.post(`/devices/${deviceId}/services/restart`, data),

  // Device commands
  killDevice: (deviceId) => axiosInstance.post(`/devices/${deviceId}/commands/kill`),
  uninstallAllServices: (deviceId) => axiosInstance.post(`/devices/${deviceId}/commands/uninstall`),
  updateServices: (deviceId, serviceName = null, version = null) => {
    const params = {};
    if (serviceName) params.serviceName = serviceName;
    if (version) params.version = version;
    return axiosInstance.post(`/devices/${deviceId}/commands/update`, params);
  },
  forceRegister: (deviceId) => axiosInstance.post(`/devices/${deviceId}/commands/register`),

  // Bulk actions on multiple devices
  bulkAction: (actionData) => axiosInstance.post('/devices/bulk-action', actionData),

  // Get monitoring history for a device
  getMonitoringHistory: (id, params) => axiosInstance.get(`/devices/${id}/monitoring-history`, { params }),

  // Get resident-app connectivity heartbeat history (network/internet/api/sip/mqtt).
  getConnectivity: (id, params) => axiosInstance.get(`/devices/${id}/connectivity`, { params }),

  // Get device variables (reported by caro-agent + services)
  getDeviceVars: (deviceUUID) => axiosInstance.get(`/einkDisplays/${deviceUUID}/variables`),

  // ── Device health (two-dimensional reachability + service health) ──
  // Spec: specs/20260625-device-health-visualization/spec.md §5.3.
  // Backend: feat/device-health-aggregator (REST live once migrations applied).
  // Current `DeviceHealthSnapshot` for one device.
  getHealth: (id) => axiosInstance.get(`/devices/${id}/health`),
  // Filterable list of snapshots for the dashboard matrix (locationId, kind).
  getHealthList: (params) => axiosInstance.get('/devices/health', { params }),
  // Transition timeline from device_health_events, most-recent-first.
  getHealthHistory: (id, params) => axiosInstance.get(`/devices/${id}/health/history`, { params }),
};

// API client for notifications
export const notificationsApi = {
  getAll: (params) => axiosInstance.get('/notifications', { params }),
  markAsRead: (id) => axiosInstance.put(`/notifications/${id}/read`),
  markAllAsRead: () => axiosInstance.put('/notifications/mark-all-read'),
  getUnreadCount: () => axiosInstance.get('/notifications/unread-count'),
  subscribe: (subscription) => axiosInstance.post('/notifications/subscribe', subscription),
  unsubscribe: (subscriptionId) => axiosInstance.delete(`/notifications/subscriptions/${subscriptionId}`),

  // Admin functions
  sendNotification: (notificationData) => axiosInstance.post('/notifications/admin/send', notificationData),
  getAllSentNotifications: (params) => axiosInstance.get('/notifications/admin/sent', { params }),
  getNotificationById: (id) => axiosInstance.get(`/notifications/admin/${id}`),
  updateNotification: (id, notificationData) => axiosInstance.put(`/notifications/admin/${id}`, notificationData),
  deleteNotification: (id) => axiosInstance.delete(`/notifications/admin/${id}`),
};

// API client for users
export const usersApi = {
  getAll: () => axiosInstance.get('/users'),
  getById: (id) => axiosInstance.get(`/users/${id}`),
  update: (id, userData) => axiosInstance.put(`/users/${id}`, userData),
  updateProfile: (userData) => axiosInstance.put('/users/profile', userData),
  updatePreferences: (preferences) => axiosInstance.put('/users/preferences', preferences),

  // Permission management
  // Optional { companyId } narrows the list, for the /company-management deep link
  // (spec-02 T057). Server-side it can only NARROW: a non-admin is already pinned to their
  // own company, so this never widens what anyone can see.
  getCompanyUsers: (params = {}) =>
    axiosInstance.get('/permissions/company-users', {
      params: params.companyId ? { companyId: params.companyId } : {},
    }),
  getMyPermissions: () => axiosInstance.get('/permissions/me'),
  getUserPermissions: (userId) => axiosInstance.get(`/permissions/user/${userId}`),
  updateUserPermissions: (userId, permissions) => axiosInstance.put(`/permissions/user/${userId}`, permissions),
  deleteUser: (userId) => axiosInstance.delete(`/users/${userId}`),

  // ── ACCOUNT LIFECYCLE (spec 20260826-company-user-management §3) ───────────
  //
  // Until this shipped there was no POST /api/users at all -- portal accounts existed
  // only because someone signed in with MS365 for the first time, or because CARO
  // approved an organisation. A customer on email+password sign-in could not add a
  // second account at all.
  //
  // All four are gated on `users.manage` server-side and take the company from the
  // CALLER's token. There is deliberately no delete: audit rows, tickets and alarms
  // reference user ids, so an account is deactivated, never removed (LD-4).

  /** Create + invite. `{ username, email, phone?, role? }`. 409 EMAIL_IN_USE per LD-1. */
  createUser: (payload) => axiosInstance.post('/users', payload),

  /** Re-issue the set-password link. Invalidates the previous one. */
  resendInvite: (userId) => axiosInstance.post(`/users/${userId}/invite`),

  /** Deactivate / reactivate. `{ active: boolean }`. */
  setUserActive: (userId, active) => axiosInstance.patch(`/users/${userId}/status`, { active }),

  /**
   * Which roles the CALLER may assign. Server-derived, so the dropdown can never offer a
   * role the API will then refuse (LD-5).
   */
  getAssignableRoles: () => axiosInstance.get('/users/meta/roles'),
};

// API client for logs
export const logsApi = {
  getLogs: (params) => axiosInstance.get('/logs', { params }),
  getByLocation: (locationId, params) => axiosInstance.get(`/logs/location/${locationId}`, { params }),
  getByDevice: (deviceId, params) => axiosInstance.get(`/logs/device/${deviceId}`, { params }),
  getByUser: (userId, params) => axiosInstance.get(`/logs/user/${userId}`, { params }),
  getByTicket: (ticketId, params) => axiosInstance.get(`/logs/ticket/${ticketId}`, { params }),
  exportLogs: (params) => axiosInstance.get('/logs/export', { params, responseType: 'blob' }),
  getStats: () => axiosInstance.get('/logs/stats'),
  clearLogs: () => axiosInstance.post('/logs/clear')
};

// Camera's als eerstelijns toestel (spec 20260831-cameras-als-toestel, fase 1).
//
// ⚠ Dit is LIVE beeld en heeft niets te maken met `cameraFootageRequestsApi` hieronder,
// dat over OPGENOMEN beeld achteraf gaat (SiteManager → DPO, met legal_basis en
// retention_period_days). Andere verwerking, andere permissiefamilie, ander endpoint.
// De twee door elkaar halen is precies hoe een GDPR-gate zijn betekenis verliest.
export const camerasApi = {
  getAll: (params) => axiosInstance.get('/cameras', { params }),
  getById: (id) => axiosInstance.get(`/cameras/${id}`),
  create: (data) => axiosInstance.post('/cameras', data),
  update: (id, data) => axiosInstance.patch(`/cameras/${id}`, data),
  delete: (id) => axiosInstance.delete(`/cameras/${id}`),

  /**
   * Alle kanalen van één NVR in één handeling.
   *
   * Payload: `{ rtspUrlTemplate, channelFrom, channelTo, namePrefix, username, password,
   * locationId?, model?, hasAudio? }` — het sjabloon draagt de plaatshouder `{channel}`.
   *
   * ⚠ **Deelsucces is de normale uitkomst.** Er zit geen transactie om de kanalen heen:
   * mislukt kanaal 17, dan blijven 1 t/m 16 bestaan. Het antwoord draagt daarom
   * `{ bulkRef, created, failed, results[] }` met een regel per kanaal, en dat moet de
   * aanroeper ook tonen. Alleen op `created` afgaan leest als "klaar" terwijl de helft
   * ontbreekt — en `pathSynced: false` betekent een camera die bestaat en zwart beeld
   * geeft, wat er in de lijst precies zo uitziet als een geslaagde.
   *
   * Zelfde permissie als de enkele aanmaak (`cameras.edit`): minder typewerk, geen andere
   * bevoegdheid.
   */
  createBulk: (data) => axiosInstance.post('/cameras/bulk', data),

  // Mint een vrijgave van 180 seconden en krijg de WHEP/HLS-URL's terug.
  //
  // Elke keer opnieuw aanroepen, niet cachen. De vrijgave IS de grens: een bewaarde URL
  // hergebruiken zou het aftellen omzeilen, en dat is precies wat het ontwerp uitsluit.
  // Er is geen verlengendpoint, en dat is opzet.
  openPreview: (id) => axiosInstance.post(`/cameras/${id}/preview`),
  // De uitslag van de verbindingstest vastleggen. Voor een camera is dit de enige
  // echte gezondheidsmeting: hij stuurt geen heartbeat, en een ping naar de recorder
  // zegt niets over dit kanaal.
  recordStreamCheck: (id, outcome) =>
    axiosInstance.post(`/cameras/${id}/stream-check`, outcome),

  // ── De koppeling: wie mag deze camera zien (fase 2) ──────────────────────
  //
  // ⚠ Alles hieronder zit achter `cameras.manage_links`, behalve `getUnits` en
  // `getReferences`. Die sleutel staat in PROTECTED_PERMISSIONS: hij bepaalt wie er
  // meekijkt, en dat is een privacybeslissing, geen beheertaak.

  /** De appartementen die deze camera al mogen zien, in sort_order. */
  getUnits: (id) => axiosInstance.get(`/cameras/${id}/units`),

  /**
   * De koppelbare appartementen — HARD begrensd tot de site van de camera.
   *
   * `search` is comfort; de sitegrens zit in het endpoint. Dit is niet een standaardfilter
   * die je kunt wegklikken maar de enige lijst die de server kent, en dat is opzet: een
   * bewoner van gebouw A heeft niets te zoeken bij de inkomhal van gebouw B, ook al is het
   * dezelfde zorggroep.
   */
  getLinkableUnits: (id, params) =>
    axiosInstance.get(`/cameras/${id}/linkable-units`, { params }),

  /** Koppelen. Accepteert MEERDERE unit-id's; één afgekeurd id laat het hele verzoek falen. */
  linkUnits: (id, payload) => axiosInstance.post(`/cameras/${id}/units`, payload),

  /** Uitrol over een heel gebouw — schrijft expliciete rijen per appartement. */
  bulkLinkBuilding: (id, payload) =>
    axiosInstance.post(`/cameras/${id}/units/bulk-building`, payload),

  unlinkUnit: (id, unitId) => axiosInstance.delete(`/cameras/${id}/units/${unitId}`),

  /** Wat verwijst er nog naar deze camera? Ophalen vóór het verwijderscherm (FR-21). */
  getReferences: (id) => axiosInstance.get(`/cameras/${id}/references`),
};

// Meldingsgeluiden (spec 20260831-domotica-meldingen-bewonerstablet, fase 2 / T-2.12).
//
// ⚠ Gebruikt bewust de BESTAANDE `axiosInstance` en niet `createApiClient()` uit
// `@caro/shared/api`. CLAUDE.md is daarover aantoonbaar verouderd: het portaal heeft nooit
// die fabriek gebruikt, en hem hier introduceren zou een DERDE API-laag opleveren met een
// eigen tokenvernieuwing en een eigen sessieafhandeling — twee dingen die precies één keer
// mogen bestaan.
export const notificationSoundsApi = {
  getAll: (params) => axiosInstance.get('/notification-sounds', { params }),

  /**
   * Uploaden. `FormData` met veld `file`; de Content-Type wordt BEWUST niet gezet, zodat de
   * browser zelf de multipart-boundary invult. Hem handmatig op `multipart/form-data`
   * zetten laat die boundary weg en dan ziet de server een leeg bestand.
   *
   * De validatie (MIME + magic bytes + duur + grootte) zit op de SERVER. Wat hier gebeurt is
   * comfort: een 20MB-bestand tegenhouden vóór het over de lijn gaat.
   */
  upload: (formData) => axiosInstance.post('/notification-sounds', formData),

  /** Vóór het verwijderscherm: "dit geluid wordt gebruikt in N flows". */
  getReferences: (id) => axiosInstance.get(`/notification-sounds/${id}/references`),

  /** SOFT-delete. Wat er al naar verwijst blijft klinken; het verdwijnt uit de kiezer. */
  delete: (id) => axiosInstance.delete(`/notification-sounds/${id}`),
};

// Achtergrondafbeeldingen voor bewonersmeldingen (spec
// 20260831-domotica-meldingen-bewonerstablet §7).
//
// ⚠ De portaalkant krijgt hier ALLEEN metadata — label, formaat, checksum — en nooit een
// bruikbare afbeeldings-URL. De bytes lopen over een aparte, ondertekende leverweg die per
// verzending wordt uitgegeven; een stabiele URL hier zou het onderscheid dat de hele
// achtergrondketen draagt (configuratie kent een `assetId`, de tablet krijgt een `url`)
// weer ongedaan maken.
//
// ⚠ NIET `createApiClient()` — om dezelfde reden die verderop op `ideasApi` staat: CLAUDE.md
// beweert dat beide apps die factory gebruiken en het portaal doet dat niet. De
// `axiosInstance` hierboven is de client.
export const notificationImagesApi = {
  // `schemaReady: false` in het antwoord betekent "de migratie is nog niet toegepast" — een
  // lege lijst betekent "er is nog niets geüpload". De kiezer hoort die twee niet als
  // hetzelfde te tonen.
  getAll: (params) => axiosInstance.get('/notification-images', { params }),

  // `FormData` met veld `file`; de Content-Type wordt BEWUST niet gezet zodat de browser de
  // multipart-boundary invult. Handmatig `multipart/form-data` zetten laat die boundary weg
  // en dan ziet de server een leeg bestand.
  upload: (formData) => axiosInstance.post('/notification-images', formData),

  // SOFT-delete. Meldingen die er al naar verwijzen blijven werken; het verdwijnt uit de kiezer.
  delete: (id) => axiosInstance.delete(`/notification-images/${id}`),
};

// De camera's van één appartement (spec 20260831-cameras-als-toestel, FR-20).
//
// Staat hier en niet in `locationsApi`, omdat deze twee routes op de CAMERA-permissies
// zitten en niet op `locations.*`. Wie mag meekijken is een privacybeslissing.
export const locationCamerasApi = {
  /**
   * De camera's van dit appartement, in de volgorde die de bewoner ziet.
   *
   * Levert GEEN stream. Elke miniatuur vraagt zijn eigen `portal_preview`-vrijgave via
   * `camerasApi.openPreview` — anders zou deze pagina precies het permanente meekijken zijn
   * dat de spec uitsluit, dan in het portaal in plaats van op de tablet.
   */
  getForLocation: (locationId) => axiosInstance.get(`/locations/${locationId}/cameras`),

  /**
   * De hele volgorde in ÉÉN aanroep, niet één per verplaatsing.
   *
   * `orderedLinkIds` moet EXACT de huidige koppelingen bevatten; de server weigert een
   * onvolledige lijst met 400 `order_mismatch` in plaats van er een halve herordening van
   * te maken.
   */
  saveOrder: (locationId, orderedLinkIds) =>
    axiosInstance.put(`/locations/${locationId}/cameras/order`, { orderedLinkIds }),
};

// API client for camera footage requests
export const cameraFootageRequestsApi = {
  // Get all camera footage requests with filtering
  getAll: (params) => axiosInstance.get('/camera-footage-requests', { params }),

  // Get single camera footage request by ID
  getById: (id) => axiosInstance.get(`/camera-footage-requests/${id}`),

  // Get request timeline/audit log
  getTimeline: (id) => axiosInstance.get(`/camera-footage-requests/${id}/timeline`),

  // Create new camera footage request
  create: (requestData) => axiosInstance.post('/camera-footage-requests', requestData),

  // Site manager approval
  siteApprove: (id, data) => axiosInstance.post(`/camera-footage-requests/${id}/site-approve`, data),

  // Site manager rejection
  siteReject: (id, data) => axiosInstance.post(`/camera-footage-requests/${id}/site-reject`, data),

  // DPO approval
  dpoApprove: (id, data) => axiosInstance.post(`/camera-footage-requests/${id}/dpo-approve`, data),

  // DPO rejection
  dpoReject: (id, data) => axiosInstance.post(`/camera-footage-requests/${id}/dpo-reject`, data),

  // Support adds footage
  addFootage: (id, data) => axiosInstance.post(`/camera-footage-requests/${id}/add-footage`, data),

  // Delete camera footage request (admin only)
  delete: (id) => axiosInstance.delete(`/camera-footage-requests/${id}`),

  // Export audit report as PDF
  exportPDF: (params) => axiosInstance.get('/camera-footage-requests/export/pdf', {
    params,
    responseType: 'blob'
  }),

  // Export single request processing audit report
  exportAuditReport: (id) => axiosInstance.get(`/camera-footage-requests/${id}/export/audit-report`, {
    responseType: 'blob'
  }),
};

// API client for documentation
export const documentationApi = {
  // Get documentation list
  getList: () => axiosInstance.get('/documentation/list'),

  // Get single documentation by slug
  getBySlug: (slug) => axiosInstance.get(`/documentation/${slug}`),

  // Get list of device script documentation
  getDeviceScripts: () => axiosInstance.get('/documentation/device-scripts'),

  // Get specific device script README
  getDeviceScriptDoc: (folderName, file) => axiosInstance.get(`/documentation/device-scripts/${folderName}`, { params: file ? { file } : {} }),
};

// API client for files
//
// Documentclassificatie (specs/20260902-documentclassificatie, T-5a.30). Everything on
// `axiosInstance` (baseURL ends in `/api`) — NOT `createApiClient()` from @caro/shared; see
// permissionTemplateService.js:6-13 for why the portal has one axios instance and one token flow.
// Every DENY of the engine is a 404 with `{ error: 'File not found' }` (AC10) — callers must not
// read a 404 as "does not exist", only as "not for you".
export const filesApi = {
  // Upload file. Body may carry `folder_id`, `classification` (may only RAISE the folder default),
  // `topics` (JSON array, must include the folder topics), `entity_type`, `entity_id`.
  upload: (formData) => axiosInstance.post('/files/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),

  // Metadata + label + effective label ({ file, effective, access, access_stats })
  get: (fileId) => axiosInstance.get(`/files/${fileId}`),

  // Bytes. `inline: true` opens in the browser instead of downloading.
  download: (fileId, { inline = false } = {}) =>
    axiosInstance.get(`/files/${fileId}/download`, { params: { inline }, responseType: 'blob' }),

  // Thumbnail (images only). size: small | medium | large
  preview: (fileId, { size = 'medium' } = {}) =>
    axiosInstance.get(`/files/${fileId}/preview`, { params: { size }, responseType: 'blob' }),

  // PATCH: { classification?, topics?, folder_uuid?, reason?, tags? }. Lowering a label, removing a
  // topic, or moving to a looser path needs documents.classify + `reason` (LD-8) — 403 with a `code`.
  update: (fileId, data) => axiosInstance.patch(`/files/${fileId}`, data),

  // Listing, filtered per file by the engine. `withheld_count` only for documents.classify holders.
  list: (params) => axiosInstance.get('/files', { params }),

  // Recycle bin (same filtering)
  listDeleted: (params) => axiosInstance.get('/files/deleted', { params }),

  // Delete file (409 { code: 'LEGAL_HOLD' } when held)
  delete: (fileId, { permanent = false } = {}) => axiosInstance.delete(`/files/${fileId}`, { params: { permanent } }),

  // Restore from the recycle bin
  restore: (fileId) => axiosInstance.post(`/files/${fileId}/restore`),
};

// API client for folders (T-5a.30). A folder is visible iff the caller may read at least one file in
// it (O5); an invisible folder is 404 `{ error: 'Folder not found' }` on every route.
export const foldersApi = {
  // { parent_folder_uuid?, recursive?, include_counts?, limit?, search? }
  tree: (params) => axiosInstance.get('/folders/tree', { params }),
  get: (folderUuid) => axiosInstance.get(`/folders/${folderUuid}`),
  // { name, parent_folder_uuid?, classification?, topics?, entity_type?, entity_id?, entity_uuid?, folder_type?, color?, icon? }
  create: (data) => axiosInstance.post('/folders/create', data),
  // { name?, color?, icon?, sort_order?, classification?, topics?, reason? }
  update: (folderUuid, data) => axiosInstance.patch(`/folders/${folderUuid}`, data),
  // { target_parent_uuid, reason? } — a looser path is a downgrade (D-16)
  move: (folderUuid, data) => axiosInstance.post(`/folders/${folderUuid}/move`, data),
  remove: (folderUuid, { recursive = false, permanent = false } = {}) =>
    axiosInstance.delete(`/folders/${folderUuid}`, { params: { recursive, permanent } }),
  restore: (folderUuid) => axiosInstance.post(`/folders/${folderUuid}/restore`),
};

// API client for document classification administration (plan §9.3). 5a ships `topics.list` and
// `suggest`; the grants/exclusions/access/archive groups are the 5b/5c routes and 404 until those land.
export const documentsApi = {
  topics: {
    list: () => axiosInstance.get('/documents/topics'),
    create: (data) => axiosInstance.post('/documents/topics', data),
    update: (id, data) => axiosInstance.patch(`/documents/topics/${id}`, data),
  },
  grants: {
    list: (params) => axiosInstance.get('/documents/grants', { params }),
    create: (data) => axiosInstance.post('/documents/grants', data),
    emergency: (data) => axiosInstance.post('/documents/grants/emergency', data),
    revoke: (id, data) => axiosInstance.post(`/documents/grants/${id}/revoke`, data),
    recertify: (id) => axiosInstance.post(`/documents/grants/${id}/recertify`),
    queue: () => axiosInstance.get('/documents/grants/recertification-queue'),
  },
  exclusions: {
    list: (params) => axiosInstance.get('/documents/exclusions', { params }),
    create: (data) => axiosInstance.post('/documents/exclusions', data),
    revoke: (id, data) => axiosInstance.post(`/documents/exclusions/${id}/revoke`, data),
    extend: (id, data) => axiosInstance.post(`/documents/exclusions/${id}/extend`, data),
  },
  access: {
    forUser: (userId) => axiosInstance.get(`/documents/access/user/${userId}`),
    forFile: (fileId) => axiosInstance.get(`/documents/access/file/${fileId}`),
    report: (params) => axiosInstance.get('/documents/access-report', { params }),
  },
  // L5: deterministic suggestion for the upload/editor chip. { folderUuid?, mimeType?, entityType? }
  suggest: (params) => axiosInstance.get('/documents/suggest', { params }),
  archive: {
    summary: () => axiosInstance.get('/documents/archive/summary'),
    due: (params) => axiosInstance.get('/documents/archive/due', { params }),
    held: () => axiosInstance.get('/documents/archive/held'),
    awaiting: (params) => axiosInstance.get('/documents/archive/awaiting-classification', { params }),
    hold: (fileId, data) => axiosInstance.post(`/documents/archive/hold/${fileId}`, data),
    clearHold: (fileId, data) => axiosInstance.post(`/documents/archive/hold/${fileId}/clear`, data),
    purge: (data) => axiosInstance.post('/documents/archive/purge', data),
    policies: () => axiosInstance.get('/documents/archive/policies'),
    updatePolicy: (id, data) => axiosInstance.patch(`/documents/archive/policies/${id}`, data),
    badge: () => axiosInstance.get('/documents/archive/badge'),
  },
};

// API client for companies
export const companiesApi = {
  // Get all companies (admin only)
  getAll: (params) => axiosInstance.get('/companies', { params }),

  // Get company by ID (admin only)
  getById: (id) => axiosInstance.get(`/companies/${id}`),

  // Create new company (admin only)
  create: (companyData) => axiosInstance.post('/companies', companyData),

  // Update company (admin only)
  update: (id, companyData) => axiosInstance.put(`/companies/${id}`, companyData),

  // Delete company (admin only)
  // `force` additionally removes the company's users. The server still refuses outright if
  // the company has LOCATIONS — a company with locations is a live customer and should be
  // deactivated, not deleted. See deleteCompany in companyController.js.
  delete: (id, { force = false } = {}) =>
    axiosInstance.delete(`/companies/${id}${force ? '?force=true' : ''}`),

  // Get company users (admin only)
  getUsers: (id, params) => axiosInstance.get(`/companies/${id}/users`, { params }),

  // Get company locations (admin only)
  getLocations: (id) => axiosInstance.get(`/companies/${id}/locations`),

  // Get company statistics (admin only)
  getStatistics: (id) => axiosInstance.get(`/companies/${id}/statistics`),

  // Get support types for current user's company (all users)
  getSupportTypes: () => axiosInstance.get('/companies/support-types'),
};

// API client for field interventions
export const fieldInterventionsApi = {
  // Get all field intervention reports with filtering and pagination
  getAll: (params) => axiosInstance.get('/field-interventions', { params }),

  // Get initialization data for new intervention
  getNew: () => axiosInstance.get('/field-interventions/new'),

  // Get single field intervention report by ID
  getById: (id) => axiosInstance.get(`/field-interventions/${id}`),

  // Create new field intervention report (manually)
  create: (interventionData) => axiosInstance.post('/field-interventions', interventionData),

  // Create field intervention from existing ticket
  createFromTicket: (ticketId) => axiosInstance.post(`/field-interventions/from-ticket/${ticketId}`),

  // Update field intervention report
  update: (id, interventionData) => axiosInstance.put(`/field-interventions/${id}`, interventionData),

  // Close field intervention report with outcome
  close: (id, outcomeData) => axiosInstance.post(`/field-interventions/${id}/close`, outcomeData),

  // Mark field intervention as invoiced
  markAsInvoiced: (id, invoicedData = { invoiced: true }) => axiosInstance.post(`/field-interventions/${id}/invoice`, invoicedData),

  // Flow B (contract-covered finalize, spec 20260702, T049/T051). Snapshots the
  // report's cost lines into a terminal `contract_covered` estimate. Path from the
  // shared API_ENDPOINTS (INTERVENTION_REPORT_FINALIZE_CONTRACT_COVERED).
  // body = { lines: TicketEstimateLineInput[] }.
  finalizeContractCovered: (id, body) =>
    axiosInstance.post(
      stripApiPrefix(API_ENDPOINTS.INTERVENTION_REPORT_FINALIZE_CONTRACT_COVERED(id)),
      body
    ),

  // Generate PDF report for intervention
  getPDF: (id) => axiosInstance.get(`/field-interventions/${id}/pdf`, {
    responseType: 'blob',
    timeout: 60000 // PDF generation might take longer
  }),

  // Delete field intervention report (admin only)
  delete: (id) => axiosInstance.delete(`/field-interventions/${id}`),
};

// Repair estimates — device catalog + tax matrix (spec 20260702-repair-estimates-catalog).
// Paths come ONLY from the shared @caro/shared API_ENDPOINTS (single source of truth,
// LD-ENDPOINTS). `axiosInstance` is already based at `${API_BASE_URL}/api`, and the
// shared constants carry the full `/api/...` path, so we strip the leading `/api`
// segment before handing the path to axios.
const stripApiPrefix = (p) => p.replace(/^\/api(?=\/)/, '');

// Leveranciers, phase 1 (spec 20260930-leveranciers-tickets-interventies §8.1, task T113).
// Paths come ONLY from the shared API_ENDPOINTS, on the portal's one axiosInstance.
//
// Wire format: the shared `Supplier` / `SupplierRoutingRule` shapes are camelCase, so the
// supplier bodies are camelCase too. The ticket action bodies follow the shared endpoint
// docs, which are snake_case: { supplier_uuid, message? }, { reason }, { reason_code,
// reason_text }, { user_id }. Every route 404s while SUPPLIERS_ENABLED is below phase 1.
export const suppliersApi = {
  // GET -> { phase, isSupplierMember? }. Auth only, no key (PD-2).
  feature: () => axiosInstance.get(stripApiPrefix(API_ENDPOINTS.SUPPLIERS_FEATURE)),
  // GET -> { suppliers: Supplier[] }. suppliers.view.
  // params: { includeArchived?: 1, allCompanies?: 1 | companyId?: n } (organisation filter: cross-org only)
  list: (params = {}) => axiosInstance.get(stripApiPrefix(API_ENDPOINTS.SUPPLIERS), { params }),
  // GET -> { supplier }. 404 outside the tenant.
  get: (uuid) => axiosInstance.get(stripApiPrefix(API_ENDPOINTS.SUPPLIER(uuid))),
  // POST { kind, name, contactName?, contactEmail?, contactPhone?, notes?, companyId? } -> { supplier }
  // companyId = the owner, cross-org users only (1 = CARO, LD-23); anyone else gets 403.
  create: (body) => axiosInstance.post(stripApiPrefix(API_ENDPOINTS.SUPPLIERS), body),
  // PATCH (changed fields only) -> { supplier }. billingMode/linkStatus are never sent.
  update: (uuid, body) => axiosInstance.patch(stripApiPrefix(API_ENDPOINTS.SUPPLIER(uuid)), body),
  // POST -> { supplier }; 409 SUPPLIER_HAS_OPEN_ASSIGNMENTS { count }.
  archive: (uuid) => axiosInstance.post(stripApiPrefix(API_ENDPOINTS.SUPPLIER_ARCHIVE(uuid))),
  // PUT { locationIds, servesAllSites? } -> { supplier, warnings? }. suppliers.assign_locations,
  // sites only. servesAllSites: CARO-owned suppliers only (LD-23); a tenant's is ignored.
  setLocations: (uuid, locationIds, extra = {}) =>
    axiosInstance.put(stripApiPrefix(API_ENDPOINTS.SUPPLIER_LOCATIONS(uuid)), { locationIds, ...extra }),
  // PUT { userIds } -> { supplier }. Internal suppliers, own users only.
  setMembers: (uuid, userIds) =>
    axiosInstance.put(stripApiPrefix(API_ENDPOINTS.SUPPLIER_MEMBERS(uuid)), { userIds }),
  routingRules: {
    // GET -> { rules: SupplierRoutingRule[] }. params: { supplierUuid? }
    list: (params = {}) =>
      axiosInstance.get(stripApiPrefix(API_ENDPOINTS.SUPPLIER_ROUTING_RULES), { params }),
    // POST { supportType, siteLocationId|null, supplierUuid, isActive } -> { rule }
    create: (body) => axiosInstance.post(stripApiPrefix(API_ENDPOINTS.SUPPLIER_ROUTING_RULES), body),
    update: (id, body) =>
      axiosInstance.patch(stripApiPrefix(API_ENDPOINTS.SUPPLIER_ROUTING_RULE(id)), body),
    remove: (id) => axiosInstance.delete(stripApiPrefix(API_ENDPOINTS.SUPPLIER_ROUTING_RULE(id))),
  },
  // ── on a ticket ──
  // GET -> TicketSupplierAssignmentsView { current, history, viewer, technicianCandidates? }
  ticketAssignments: (ticketId) =>
    axiosInstance.get(stripApiPrefix(API_ENDPOINTS.TICKET_SUPPLIER_ASSIGNMENTS(ticketId))),
  // GET -> { candidates: TicketSupplierCandidate[], suggestedSupplierUuid }
  ticketCandidates: (ticketId) =>
    axiosInstance.get(stripApiPrefix(API_ENDPOINTS.TICKET_SUPPLIER_CANDIDATES(ticketId))),
  // POST { supplier_uuid, message? } -> { assignment }
  forwardTicket: (ticketId, body) =>
    axiosInstance.post(stripApiPrefix(API_ENDPOINTS.TICKET_FORWARD_TO_SUPPLIER(ticketId)), body),
  // POST { reason? } -> { assignment }; reason (>= 10 chars) required once accepted.
  withdrawAssignment: (ticketId, body = {}) =>
    axiosInstance.post(stripApiPrefix(API_ENDPOINTS.TICKET_SUPPLIER_ASSIGNMENT_WITHDRAW(ticketId)), body),
  // POST -> { assignment }. Members of the assigned internal supplier.
  acceptAssignment: (ticketId) =>
    axiosInstance.post(stripApiPrefix(API_ENDPOINTS.TICKET_SUPPLIER_ASSIGNMENT_ACCEPT(ticketId))),
  // POST { reason_code, reason_text } -> { assignment }
  refuseAssignment: (ticketId, body) =>
    axiosInstance.post(stripApiPrefix(API_ENDPOINTS.TICKET_SUPPLIER_ASSIGNMENT_REFUSE(ticketId)), body),
  // PUT { user_id } -> { assignment }
  setAssignmentTechnician: (ticketId, body) =>
    axiosInstance.put(stripApiPrefix(API_ENDPOINTS.TICKET_SUPPLIER_ASSIGNMENT_TECHNICIAN(ticketId)), body),
};

// Paid module entitlements (spec 20260818-invoicingsystem/spec-00-modules.md §5.3).
// Paths come ONLY from the shared API_ENDPOINTS.
export const modulesApi = {
  // GET /api/v1/modules/me — the caller's own company. Authentication only, no
  // permission: every user must be able to learn which surfaces exist before the portal
  // can decide what to render.
  getMine: () => axiosInstance.get(stripApiPrefix(API_ENDPOINTS.MODULES_ME)),

  // The three below need `modules.manage`, which is a PROTECTED permission — a
  // customer's own company_admin does NOT inherit it (D25).
  listCompanies: () => axiosInstance.get(stripApiPrefix(API_ENDPOINTS.MODULES_COMPANIES)),
  getCompany: (companyId) =>
    axiosInstance.get(stripApiPrefix(API_ENDPOINTS.MODULES_COMPANY(companyId))),
  // body = { enabled: boolean, validUntil?: string|null, contractRef?: string, note?: string }
  setCompanyModule: (companyId, moduleKey, body) =>
    axiosInstance.put(
      stripApiPrefix(API_ENDPOINTS.MODULES_COMPANY_MODULE(companyId, moduleKey)),
      body
    ),
};

// Resident invoicing — configuration tier (spec 20260818-invoicingsystem spec-01 §6).
// Every call is gated server-side on BOTH the `invoicing` module and the
// `invoicing.config` permission; a 403 body carries `code` so the UI can tell
// MODULE_NOT_ACTIVE apart from a permission refusal.
// `companyId` is optional and only honoured for admin/super_admin — the API refuses a
// cross-organisation target from anyone else with CROSS_ORG_DENIED. Omitting it means
// "my own organisation", which is what a customer always sends.
const orgParams = (companyId, extra) => {
  const p = { ...(extra || {}) };
  if (companyId) p.companyId = companyId;
  return Object.keys(p).length ? p : undefined;
};

/**
 * An APARTMENT's payers and invoice recipients.
 *
 * Separate from the person-addressed `billing-parties` routes: a flat has TWO liable
 * parties (owner and resident) plus any number of people who only receive a copy. The
 * unit id is a PATH parameter because it is what the server's tenancy walk runs on —
 * the site is derived from it and is never sent.
 */
export const unitBillingApi = {
  /** Parties, recipients, AND what the flat resolves to today. */
  getForUnit: (unitLocationId, companyId) =>
    axiosInstance.get(stripApiPrefix(API_ENDPOINTS.INVOICING_UNIT_BILLING(unitLocationId)), {
      params: orgParams(companyId),
    }),

  createParty: (unitLocationId, body, companyId) =>
    axiosInstance.post(
      stripApiPrefix(API_ENDPOINTS.INVOICING_UNIT_BILLING_PARTIES(unitLocationId)),
      body,
      { params: orgParams(companyId) }
    ),

  updateParty: (uuid, body, companyId) =>
    axiosInstance.patch(
      stripApiPrefix(API_ENDPOINTS.INVOICING_UNIT_BILLING_PARTY(uuid)),
      body,
      { params: orgParams(companyId) }
    ),

  /** Refused with PARTY_IN_FORCE once it has ever applied — close it instead. */
  deleteParty: (uuid, companyId) =>
    axiosInstance.delete(stripApiPrefix(API_ENDPOINTS.INVOICING_UNIT_BILLING_PARTY(uuid)), {
      params: orgParams(companyId),
    }),

  createRecipient: (unitLocationId, body, companyId) =>
    axiosInstance.post(
      stripApiPrefix(API_ENDPOINTS.INVOICING_UNIT_RECIPIENTS(unitLocationId)),
      body,
      { params: orgParams(companyId) }
    ),

  updateRecipient: (uuid, body, companyId) =>
    axiosInstance.patch(stripApiPrefix(API_ENDPOINTS.INVOICING_UNIT_RECIPIENT(uuid)), body, {
      params: orgParams(companyId),
    }),

  deleteRecipient: (uuid, companyId) =>
    axiosInstance.delete(stripApiPrefix(API_ENDPOINTS.INVOICING_UNIT_RECIPIENT(uuid)), {
      params: orgParams(companyId),
    }),
};

export const invoicingConfigApi = {
  getIssuers: (siteId, companyId) =>
    axiosInstance.get(stripApiPrefix(API_ENDPOINTS.INVOICING_ISSUERS), {
      params: orgParams(companyId, siteId ? { siteId } : null),
    }),
  putOrganisationIssuer: (body, companyId) =>
    axiosInstance.put(stripApiPrefix(API_ENDPOINTS.INVOICING_ISSUERS), body, {
      params: orgParams(companyId),
    }),
  putSiteIssuer: (siteId, body, companyId) =>
    axiosInstance.put(
      `${stripApiPrefix(API_ENDPOINTS.INVOICING_ISSUERS)}/sites/${siteId}`,
      body,
      { params: orgParams(companyId) }
    ),

  getAccountingConfig: (siteId, companyId) =>
    axiosInstance.get(stripApiPrefix(API_ENDPOINTS.INVOICING_ACCOUNTING_CONFIG), {
      params: orgParams(companyId, siteId ? { siteId } : null),
    }),
  // body = { siteId?, entries: [...] }
  putAccountingConfig: (body, companyId) =>
    axiosInstance.put(stripApiPrefix(API_ENDPOINTS.INVOICING_ACCOUNTING_CONFIG), body, {
      params: orgParams(companyId),
    }),
  // Reverting an override DELETES the site row so the subdivision inherits again.
  revertAccountingOverride: (siteId, subdivisionCode, companyId) =>
    axiosInstance.delete(
      `${stripApiPrefix(API_ENDPOINTS.INVOICING_ACCOUNTING_CONFIG)}/${siteId}/${subdivisionCode}`,
      { params: orgParams(companyId) }
    ),
};

// Resident invoicing — billable-item ledger + billing parties (spec-01 §6, Phase 3).
// Money fields are stripped SERVER-side when the caller lacks `invoicing.view_amounts`
// (a PROTECTED permission), so the response shape varies by grant; `meta.withAmounts`
// tells the UI which it got rather than making it guess from missing keys.
export const invoicingLedgerApi = {
  // params: { personId?, siteId?, sourceType?, status?: 'open'|'invoiced'|'excluded', from?, to?, limit? }
  listItems: (params, companyId) =>
    axiosInstance.get(stripApiPrefix(API_ENDPOINTS.INVOICING_ITEMS), {
      params: orgParams(companyId, params),
    }),
  createItem: (body, companyId) =>
    axiosInstance.post(stripApiPrefix(API_ENDPOINTS.INVOICING_ITEMS), body, {
      params: orgParams(companyId),
    }),
  // Excluding holds an item out of invoicing; it never deletes, because the consumption
  // still belongs in the site report.
  excludeItem: (uuid, reason, companyId) =>
    axiosInstance.post(stripApiPrefix(API_ENDPOINTS.INVOICING_ITEM_EXCLUDE(uuid)), { reason }, {
      params: orgParams(companyId),
    }),

  listBillingParties: (personId, companyId) =>
    axiosInstance.get(stripApiPrefix(API_ENDPOINTS.INVOICING_BILLING_PARTIES), {
      params: orgParams(companyId, { personId }),
    }),
  createBillingParty: (body, companyId) =>
    axiosInstance.post(stripApiPrefix(API_ENDPOINTS.INVOICING_BILLING_PARTIES), body, {
      params: orgParams(companyId),
    }),
  updateBillingParty: (uuid, body, companyId) =>
    axiosInstance.patch(stripApiPrefix(API_ENDPOINTS.INVOICING_BILLING_PARTY(uuid)), body, {
      params: orgParams(companyId),
    }),
};

// Resident invoicing — documents + payments (spec-01 §6, Phase 4).
export const invoicingInvoiceApi = {
  list: (params, companyId) =>
    axiosInstance.get(stripApiPrefix(API_ENDPOINTS.INVOICING_INVOICES), {
      params: orgParams(companyId, params),
    }),
  get: (uuid, companyId) =>
    axiosInstance.get(stripApiPrefix(API_ENDPOINTS.INVOICING_INVOICE(uuid)), {
      params: orgParams(companyId),
    }),
  // body = { personId, itemUuids: [...], siteLocationId?, periodStart?, periodEnd? }
  createDraft: (body, companyId) =>
    axiosInstance.post(stripApiPrefix(API_ENDPOINTS.INVOICING_INVOICES), body, {
      params: orgParams(companyId),
    }),
  remove: (uuid, companyId) =>
    axiosInstance.delete(stripApiPrefix(API_ENDPOINTS.INVOICING_INVOICE(uuid)), {
      params: orgParams(companyId),
    }),
  // Consumes a gapless number and freezes the document. Separate permission on purpose.
  issue: (uuid, companyId) =>
    axiosInstance.post(stripApiPrefix(API_ENDPOINTS.INVOICING_INVOICE_ISSUE(uuid)), {}, {
      params: orgParams(companyId),
    }),
  send: (uuid, body, companyId) =>
    axiosInstance.post(stripApiPrefix(API_ENDPOINTS.INVOICING_INVOICE_SEND(uuid)), body || {}, {
      params: orgParams(companyId),
    }),
  creditNote: (uuid, body, companyId) =>
    axiosInstance.post(
      stripApiPrefix(API_ENDPOINTS.INVOICING_INVOICE_CREDIT_NOTE(uuid)),
      body || {},
      { params: orgParams(companyId) }
    ),
  // Opened in a new tab, so it needs an absolute URL rather than an axios call.
  pdfUrl: (uuid) => `${API_BASE_URL}${API_ENDPOINTS.INVOICING_INVOICE_PDF(uuid)}`,
  exportUrl: (from, to) =>
    `${API_BASE_URL}${API_ENDPOINTS.INVOICING_EXPORT}?from=${from || ''}&to=${to || ''}`,
};

export const invoicingPaymentApi = {
  /** A hosted-checkout link for an issued invoice. Created once and reused. */
  payLink: (uuid, companyId) =>
    axiosInstance.post(
      stripApiPrefix(API_ENDPOINTS.INVOICING_INVOICE_PAY_LINK(uuid)),
      {},
      { params: orgParams(companyId) }
    ),
  list: (params, companyId) =>
    axiosInstance.get(stripApiPrefix(API_ENDPOINTS.INVOICING_PAYMENTS), {
      params: orgParams(companyId, params),
    }),
  create: (body, companyId) =>
    axiosInstance.post(stripApiPrefix(API_ENDPOINTS.INVOICING_PAYMENTS), body, {
      params: orgParams(companyId),
    }),
  allocate: (uuid, body, companyId) =>
    axiosInstance.post(stripApiPrefix(API_ENDPOINTS.INVOICING_PAYMENT_ALLOCATIONS(uuid)), body, {
      params: orgParams(companyId),
    }),
  deallocate: (uuid, id, companyId) =>
    axiosInstance.delete(
      stripApiPrefix(API_ENDPOINTS.INVOICING_PAYMENT_ALLOCATION(uuid, id)),
      { params: orgParams(companyId) }
    ),
};

// ── Cafeteria POS (spec 20260818-invoicingsystem/spec-02-pos.md) ─────────────
//
// The PORTAL half only: catalog, configuration, restrictions, terminals, day closes and
// reports. The selling endpoints (/bootstrap, /identify, /sales) are deliberately absent —
// they need a terminal token on top of the staff session and belong to the till app
// (packages/pos-app), not to a browser tab that anyone with a staff login can open.
//
// Same orgParams rule as invoicing: companyId is honoured only for admin/super_admin.
export const posApi = {
  getConfig: (siteId, companyId) =>
    axiosInstance.get(stripApiPrefix(API_ENDPOINTS.POS_CONFIG(siteId)), {
      params: orgParams(companyId),
    }),
  putConfig: (siteId, body, companyId) =>
    axiosInstance.put(stripApiPrefix(API_ENDPOINTS.POS_CONFIG(siteId)), body, {
      params: orgParams(companyId),
    }),

  listCategories: (siteId, companyId) =>
    axiosInstance.get(stripApiPrefix(API_ENDPOINTS.POS_CATEGORIES), {
      params: orgParams(companyId, siteId ? { siteId } : null),
    }),
  upsertCategory: (body, companyId) =>
    axiosInstance.put(stripApiPrefix(API_ENDPOINTS.POS_CATEGORIES), body, {
      params: orgParams(companyId),
    }),

  listProducts: (siteId, companyId) =>
    axiosInstance.get(stripApiPrefix(API_ENDPOINTS.POS_PRODUCTS), {
      params: orgParams(companyId, siteId ? { siteId } : null),
    }),
  // PUT, not POST: the body carries the uuid when it is an edit. One endpoint, so a
  // half-written product cannot end up as a second row.
  upsertProduct: (body, companyId) =>
    axiosInstance.put(stripApiPrefix(API_ENDPOINTS.POS_PRODUCTS), body, {
      params: orgParams(companyId),
    }),
  /**
   * Remove a product from the catalogue.
   *
   * The server decides DELETE-or-ARCHIVE from the data, not from a flag here: a product
   * that was ever sold cannot be removed without taking its sale lines with it, and those
   * are the accounting record. The response says which happened in `data.outcome`, and the
   * caller must report that rather than assuming "deleted".
   */
  deleteProduct: (uuid, companyId) =>
    axiosInstance.delete(stripApiPrefix(API_ENDPOINTS.POS_PRODUCT(uuid)), {
      params: orgParams(companyId),
    }),
  /**
   * Adjust stock: `{ delta }` for a delivery, `{ set }` for a stocktake. Exactly one.
   *
   * Its own endpoint rather than a product PUT so a delivery is one request instead of a
   * read-modify-write of the whole product, and so the audit trail can name the act.
   */
  adjustStock: (uuid, body, companyId) =>
    axiosInstance.post(stripApiPrefix(API_ENDPOINTS.POS_PRODUCT_STOCK(uuid)), body, {
      params: orgParams(companyId),
    }),
  /** Refuses (409 CATEGORY_NOT_EMPTY) while the category still holds active products. */
  deleteCategory: (uuid, companyId) =>
    axiosInstance.delete(stripApiPrefix(API_ENDPOINTS.POS_CATEGORY(uuid)), {
      params: orgParams(companyId),
    }),

  // Room PINs for unattended selling. A room code is not a secret (it is on the door) and
  // is returned in the clear; the PIN never comes back in any form.
  // Card payment (Viva). Reads report STATE — the client secret is never returned, by
  // anyone, including whoever typed it.
  getPaymentConfig: (siteId, companyId) =>
    axiosInstance.get(stripApiPrefix(API_ENDPOINTS.POS_PAYMENT_CONFIG), {
      params: orgParams(companyId, siteId ? { siteId } : null),
    }),
  putPaymentConfig: (body, companyId) =>
    axiosInstance.put(stripApiPrefix(API_ENDPOINTS.POS_PAYMENT_CONFIG), body, {
      params: orgParams(companyId),
    }),
  testPaymentConfig: (body, companyId) =>
    axiosInstance.post(stripApiPrefix(API_ENDPOINTS.POS_PAYMENT_CONFIG_TEST), body, {
      params: orgParams(companyId),
    }),

  listRoomPins: (siteId, companyId) =>
    axiosInstance.get(stripApiPrefix(API_ENDPOINTS.POS_ROOM_PINS), {
      params: orgParams(companyId, siteId ? { siteId } : null),
    }),
  upsertRoomPin: (body, companyId) =>
    axiosInstance.put(stripApiPrefix(API_ENDPOINTS.POS_ROOM_PINS), body, {
      params: orgParams(companyId),
    }),
  // Derive a room number for every unit from its location name. WITHOUT `apply` this is a
  // dry run and writes nothing — the portal shows the proposal first, because these codes
  // end up on doors.
  generateRoomCodes: (body, companyId) =>
    axiosInstance.post(stripApiPrefix(`${API_ENDPOINTS.POS_ROOM_PINS}/generate`), body, {
      params: orgParams(companyId),
    }),

  listRestrictions: (params, companyId) =>
    axiosInstance.get(stripApiPrefix(API_ENDPOINTS.POS_RESTRICTIONS), {
      params: orgParams(companyId, params),
    }),
  createRestriction: (body, companyId) =>
    axiosInstance.post(stripApiPrefix(API_ENDPOINTS.POS_RESTRICTIONS), body, {
      params: orgParams(companyId),
    }),
  // Revoking sets revoked_at; the row survives, because "who lifted this and when" is the
  // part anyone will actually ask about later.
  revokeRestriction: (id, companyId) =>
    axiosInstance.delete(stripApiPrefix(`${API_ENDPOINTS.POS_RESTRICTIONS}/${id}`), {
      params: orgParams(companyId),
    }),

  // ── The house rule: one daily ceiling for every resident of a site ────────
  //
  // `available: false` in the response means the 20260826140000 migration has not been
  // applied yet — not that the site has no rules. The UI must tell those apart.
  listSiteRestrictions: (params, companyId) =>
    axiosInstance.get(stripApiPrefix(API_ENDPOINTS.POS_SITE_RESTRICTIONS), {
      params: orgParams(companyId, params),
    }),
  createSiteRestriction: (body, companyId) =>
    axiosInstance.post(stripApiPrefix(API_ENDPOINTS.POS_SITE_RESTRICTIONS), body, {
      params: orgParams(companyId),
    }),
  revokeSiteRestriction: (id, companyId) =>
    axiosInstance.delete(stripApiPrefix(API_ENDPOINTS.POS_SITE_RESTRICTION(id)), {
      params: orgParams(companyId),
    }),

  listTerminals: (siteId, companyId) =>
    axiosInstance.get(stripApiPrefix(API_ENDPOINTS.POS_TERMINALS), {
      params: orgParams(companyId, siteId ? { siteId } : null),
    }),
  // The response carries `enrolmentToken` ONCE. It is never stored in readable form and
  // never returned again — a lost token means revoke and re-enrol.
  createTerminal: (body, companyId) =>
    axiosInstance.post(stripApiPrefix(API_ENDPOINTS.POS_TERMINALS), body, {
      params: orgParams(companyId),
    }),
  revokeTerminal: (uuid, companyId) =>
    axiosInstance.post(stripApiPrefix(API_ENDPOINTS.POS_TERMINAL_REVOKE(uuid)), {}, {
      params: orgParams(companyId),
    }),

  listDayCloses: (siteId, companyId) =>
    axiosInstance.get(stripApiPrefix(API_ENDPOINTS.POS_DAY_CLOSES), {
      params: orgParams(companyId, siteId ? { siteId } : null),
    }),
  closeDay: (siteId, date, companyId) =>
    axiosInstance.post(stripApiPrefix(API_ENDPOINTS.POS_DAY_CLOSE_CLOSE(siteId, date)), {}, {
      params: orgParams(companyId),
    }),
  /**
   * De dagtelling. Eén per site per dag, en niet overschrijfbaar — een telling is een
   * momentopname.
   *
   * `openingFloatCents` en `closingFloatCents` zijn optioneel maar worden GEWEIGERD (503
   * CASH_MOVEMENTS_UNAVAILABLE) op een server waar migratie 20260826190000 nog niet
   * toegepast is, in plaats van stil genegeerd. Weglaten geeft het oude gedrag: geteld
   * tegenover de cashverkopen.
   */
  createCashCount: (body, companyId) =>
    axiosInstance.post(stripApiPrefix(API_ENDPOINTS.POS_CASH_COUNTS), body, {
      params: orgParams(companyId),
    }),

  // ── Kasbewerkingen ────────────────────────────────────────────────────────
  //
  // `available: false` in het antwoord betekent dat de migratie niet toegepast is — niet
  // dat deze site nooit iets uit de lade haalt. De UI moet die twee uit elkaar houden.
  listCashMovements: (params, companyId) =>
    axiosInstance.get(stripApiPrefix(API_ENDPOINTS.POS_CASH_MOVEMENTS), {
      params: orgParams(companyId, params),
    }),
  createCashMovement: (body, companyId) =>
    axiosInstance.post(stripApiPrefix(API_ENDPOINTS.POS_CASH_MOVEMENTS), body, {
      params: orgParams(companyId),
    }),
  // 409 CASH_DAY_FROZEN zodra de dag geteld of afgesloten is: de som zit dan vast in
  // cash_count.movements_cents en mag niet meer verschuiven.
  deleteCashMovement: (uuid, companyId) =>
    axiosInstance.delete(stripApiPrefix(API_ENDPOINTS.POS_CASH_MOVEMENT(uuid)), {
      params: orgParams(companyId),
    }),

  // `persons: 'true'` is a REQUEST, not a guarantee. Without pos.report_persons the server
  // returns byPerson: null and the UI says so, rather than silently showing a total.
  // Starter catalogue for an EMPTY cafeteria. 409s on a site that already has products —
  // seeding twice would duplicate every line beside the one someone already corrected.
  seedCatalog: (siteLocationId, companyId, pricesIncludeTax = true) =>
    axiosInstance.post(
      stripApiPrefix(`${API_ENDPOINTS.POS_PRODUCTS.replace('/products', '/catalog/seed')}`),
      { siteLocationId, pricesIncludeTax },
      { params: orgParams(companyId) }
    ),

  report: (params, companyId) =>
    axiosInstance.get(stripApiPrefix(API_ENDPOINTS.POS_REPORTS), {
      params: orgParams(companyId, params),
    }),
  /**
   * CSV export.
   *
   * Fetched through axios rather than handed to window.open. A browser navigation carries
   * no Authorization header, so the old exportUrl produced a bare 401 page —
   * "Authentication required" — on a route the user is perfectly entitled to.
   *
   * Returns a blob; the caller saves it. Same shape as every other export in this client.
   */
  exportCsv: (siteId, from, to, companyId) =>
    axiosInstance.get(stripApiPrefix(API_ENDPOINTS.POS_EXPORT), {
      params: orgParams(companyId, { siteId, from, to }),
      responseType: 'blob',
    }),

  // ── The monthly per-apartment statement ──────────────────────────────────
  //
  // `includeNames` is a REQUEST, not a guarantee: naming which resident of flat 14 consumed
  // what needs pos.report_persons, which is PROTECTED. The response reports both
  // `personsPermitted` and `personsRequested` so the UI can say which of the two is missing
  // instead of blaming the permission for a switch nobody ticked.
  statement: (siteId, params, companyId) =>
    axiosInstance.get(stripApiPrefix(API_ENDPOINTS.POS_STATEMENT(siteId)), {
      params: orgParams(companyId, params),
    }),
  // A blob, like exportCsv and for the same reason: a browser navigation carries no
  // Authorization header, so window.open on this URL yields a bare 401 page.
  statementPdf: (siteId, params, companyId) =>
    axiosInstance.get(stripApiPrefix(API_ENDPOINTS.POS_STATEMENT_PDF(siteId)), {
      params: orgParams(companyId, params),
      responseType: 'blob',
    }),
  // Mails the site address. Runs the SAME code the 1st-of-the-month job runs — never a
  // second implementation, or the two documents stop being the same document.
  sendStatement: (siteId, params, companyId) =>
    axiosInstance.post(
      stripApiPrefix(API_ENDPOINTS.POS_STATEMENT_SEND(siteId)),
      {},
      { params: orgParams(companyId, params) }
    ),
};

// ── Staff credentials (PIN today, badge next) ───────────────────────────────
//
// On the USER, not on the cafeteria: the till is the first consumer, access control is the
// intended second, and a credential belongs to a person rather than to a feature.
//
// Nothing secret ever comes back — list() returns state only. Setting your OWN PIN does not
// need users.manage; registering a badge does, because handing out a physical card is an act
// someone else signs off on.
export const staffCredentialApi = {
  list: (userId) => axiosInstance.get(`/users/${userId}/credentials`),
  setPin: (userId, pin) => axiosInstance.put(`/users/${userId}/credentials/pin`, { pin }),
  registerCard: (userId, token, label) =>
    axiosInstance.post(`/users/${userId}/credentials/card`, { token, label }),
  revoke: (userId, uuid) => axiosInstance.delete(`/users/${userId}/credentials/${uuid}`),
};

// API client for the device catalog (view-only Shop reads + admin CRUD/stock/price).
export const catalogApi = {
  // GET /api/v1/catalog/items — active items with in_stock + latest price.
  // Pass { includeInactive: true } for admin surfaces.
  listItems: (params) => axiosInstance.get(stripApiPrefix(API_ENDPOINTS.CATALOG_ITEMS), { params }),
  getItem: (id) => axiosInstance.get(stripApiPrefix(API_ENDPOINTS.CATALOG_ITEM(id))),
  createItem: (data) => axiosInstance.post(stripApiPrefix(API_ENDPOINTS.CATALOG_ITEMS), data),
  updateItem: (id, data) => axiosInstance.patch(stripApiPrefix(API_ENDPOINTS.CATALOG_ITEM(id)), data),
  deactivateItem: (id) => axiosInstance.post(stripApiPrefix(API_ENDPOINTS.CATALOG_ITEM_DEACTIVATE(id))),
  setStock: (id, inStock) =>
    axiosInstance.patch(stripApiPrefix(API_ENDPOINTS.CATALOG_ITEM_STOCK(id)), { inStock }),
  // Append a new price row + repoint current_price_id (never mutates history).
  appendPrice: (id, data) =>
    axiosInstance.post(stripApiPrefix(API_ENDPOINTS.CATALOG_ITEM_PRICE(id)), data),
};

// ── Knowledge Base read API (spec 20260706-portal-knowledge-base, Phase 1) ───
// SWAPPABLE MOCK: the CARO-Api KB endpoints (T-P1-09..13) live in a separate
// repo and are not built yet, so the portal reads from an in-memory fixture
// module (`kbMockApi`) that returns the SAME `{ data }` shape as the real API.
// The UI is therefore fully clickable now.
//
//   ↳ TO GO LIVE: set REACT_APP_KB_USE_MOCKS=false (or flip USE_KB_MOCKS to
//     `false` below). `knowledgeBaseApi` then points every method at the real
//     `axiosInstance` + `API_ENDPOINTS.KB_*` — no call-site changes needed.
//
// Paths come ONLY from the shared API_ENDPOINTS (LD-ENDPOINTS); Bearer + 401
// refresh are inherited from `axiosInstance`. Screenshots returned by the read
// API are auth-proxied via KB_ASSET.
const USE_KB_MOCKS = process.env.REACT_APP_KB_USE_MOCKS !== 'false';

const kbRealApi = {
  listPages: (locale) =>
    axiosInstance.get(stripApiPrefix(API_ENDPOINTS.KB_PAGES), {
      params: locale ? { locale } : {},
    }),
  getPage: (slug, locale) =>
    axiosInstance.get(stripApiPrefix(API_ENDPOINTS.KB_PAGE(slug)), {
      params: locale ? { locale } : {},
    }),
  getSearchIndex: (locale) =>
    axiosInstance.get(stripApiPrefix(API_ENDPOINTS.KB_SEARCH_INDEX(locale))),
  getStatus: () => axiosInstance.get(stripApiPrefix(API_ENDPOINTS.KB_STATUS)),
  getRuns: () => axiosInstance.get(stripApiPrefix(API_ENDPOINTS.KB_RUNS)),
  // Auth-proxied image URL for a KB asset (screenshot / upload).
  assetUrl: (uuid) =>
    `${axiosInstance.defaults.baseURL}${stripApiPrefix(API_ENDPOINTS.KB_ASSET(uuid))}`,

  // ── Write side (knowledge_base.manage). Not exercised until the CARO-Api KB
  // write endpoints (spec 2a) exist; wired now so mock→real is a pure flag flip.
  // Signatures are identical to the kbMockApi write methods. ──────────────────
  createPage: (data) =>
    axiosInstance.post(stripApiPrefix(API_ENDPOINTS.KB_PAGE_CREATE), data),
  updatePage: (id, data) =>
    axiosInstance.patch(stripApiPrefix(API_ENDPOINTS.KB_PAGE_UPDATE(id)), data),
  overrideSection: (id, body) =>
    axiosInstance.put(stripApiPrefix(API_ENDPOINTS.KB_SECTION_OVERRIDE(id)), body),
  revertSection: (id) =>
    axiosInstance.post(stripApiPrefix(API_ENDPOINTS.KB_SECTION_REVERT(id))),
  listRevisions: (id) =>
    axiosInstance.get(stripApiPrefix(API_ENDPOINTS.KB_PAGE_REVISIONS(id))),
  revertToRevision: (id, rev) =>
    axiosInstance.post(stripApiPrefix(API_ENDPOINTS.KB_PAGE_REVERT(id, rev))),
  // Multipart image upload → { uuid, url } (url is auth-proxied via KB_ASSET).
  uploadAsset: (file) => {
    const form = new FormData();
    form.append('file', file);
    return axiosInstance.post(stripApiPrefix(API_ENDPOINTS.KB_ASSET_UPLOAD), form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

export const knowledgeBaseApi = USE_KB_MOCKS ? kbMockApi : kbRealApi;

// API client for the DB-configurable tax matrix (CR-8).
export const taxRatesApi = {
  getAll: () => axiosInstance.get(stripApiPrefix(API_ENDPOINTS.TAX_RATES)),
  upsert: (data) => axiosInstance.post(stripApiPrefix(API_ENDPOINTS.TAX_RATES), data),
  update: (id, data) => axiosInstance.patch(stripApiPrefix(API_ENDPOINTS.TAX_RATE(id)), data),
};

// API client for in-ticket repair estimates (Phase 2: draft build → offer freeze → clone).
// Paths come ONLY from the shared API_ENDPOINTS (LD-ENDPOINTS). The builder also fetches
// the tax matrix via `taxRatesApi.getAll()` for its live (display-only) tax preview.
export const estimatesApi = {
  // GET/POST /api/v1/tickets/:ticketId/estimates — list / create draft.
  listForTicket: (ticketId) =>
    axiosInstance.get(stripApiPrefix(API_ENDPOINTS.TICKET_ESTIMATES(ticketId))),
  createForTicket: (ticketId, data) =>
    axiosInstance.post(stripApiPrefix(API_ENDPOINTS.TICKET_ESTIMATES(ticketId)), data),
  // GET/PATCH /api/v1/estimates/:id — read / edit a draft.
  getOne: (id) => axiosInstance.get(stripApiPrefix(API_ENDPOINTS.ESTIMATE(id))),
  patchDraft: (id, data) => axiosInstance.patch(stripApiPrefix(API_ENDPOINTS.ESTIMATE(id)), data),
  // POST /api/v1/estimates/:id/offer — freeze (price+tax+invoicing-email snapshot, mint token).
  offer: (id) => axiosInstance.post(stripApiPrefix(API_ENDPOINTS.ESTIMATE_OFFER(id))),
  // POST /api/v1/estimates/:id/clone — clone a terminal estimate into a fresh draft (CR-4).
  clone: (id) => axiosInstance.post(stripApiPrefix(API_ENDPOINTS.ESTIMATE_CLONE(id))),
  // DELETE /api/v1/estimates/:id — bin a DRAFT (2026-07-30). Drafts only: the
  // server 409s (NOT_DELETABLE) on anything already offered or terminal.
  deleteDraft: (id) => axiosInstance.delete(stripApiPrefix(API_ENDPOINTS.ESTIMATE(id))),
  // GET /api/v1/estimates — overview list across all tickets (2026-07-17).
  // Paginated + filterable since 2026-07-29: { status, page, limit, from, to,
  // q, siteId }. The response carries `pagination` and a per-status `summary`
  // alongside `data`. Empty/'all' values are dropped so the query stays clean.
  listAll: (params = {}) => {
    const query = {};
    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return;
      if (key === 'status' && value === 'all') return;
      query[key] = value;
    });
    return axiosInstance.get(stripApiPrefix(API_ENDPOINTS.ESTIMATES_LIST), { params: query });
  },
  // PATCH /api/v1/estimates/:id/executed — record the approved work as carried
  // out (2026-07-30). Also happens automatically when the ticket closes; this is
  // for work finished while the ticket legitimately stays open.
  markExecuted: (id) => axiosInstance.patch(stripApiPrefix(API_ENDPOINTS.ESTIMATE_EXECUTED(id))),
  // PATCH /api/v1/estimates/:id/invoiced — mark an EXECUTED approved/contract-
  // covered estimate invoiced. Server 409s (NOT_EXECUTED) if it is not yet
  // executed, so the portal keeps the button disabled until it is.
  markInvoiced: (id) => axiosInstance.patch(stripApiPrefix(API_ENDPOINTS.ESTIMATE_INVOICED(id))),
  // POST /api/v1/estimates/:id/approve — MANUAL approval by a support employee
  // (2026-07-29). Body { note } is REQUIRED (server 400s on an empty remark):
  // it records on whose authority the client accepted outside the public link.
  // Accepts an offered OR an auto-expired estimate.
  manualApprove: (id, note) =>
    axiosInstance.post(stripApiPrefix(API_ENDPOINTS.ESTIMATE_MANUAL_APPROVE(id)), { note }),
  // POST /api/v1/estimates/:id/decline — the mirror: record a refusal the
  // client gave outside the public link. Body { reason } is REQUIRED.
  manualDecline: (id, reason) =>
    axiosInstance.post(stripApiPrefix(API_ENDPOINTS.ESTIMATE_MANUAL_DECLINE(id)), { reason }),
  // GET /api/v1/estimates/:id/pdf — printable PDF (blob; authed via the interceptor).
  pdf: (id) =>
    axiosInstance.get(stripApiPrefix(API_ENDPOINTS.ESTIMATE_PDF(id)), { responseType: 'blob' }),
  // POST /api/v1/estimates/pdf-bundle — the selected estimates as ONE PDF (blob).
  // Server caps the selection at 100 (400 TOO_MANY) and drops ids outside the
  // caller's tenant silently (404 when nothing is left).
  // PATCH /api/v1/estimates/:id/comment — edit the operator comment on any
  // estimate that is not yet invoiced (2026-09-25). Server 409s INVOICED past that.
  patchComment: (id, comment) =>
    axiosInstance.patch(stripApiPrefix(API_ENDPOINTS.ESTIMATE_COMMENT(id)), { comment }),
  pdfBundle: (ids) =>
    axiosInstance.post(stripApiPrefix(API_ENDPOINTS.ESTIMATES_PDF_BUNDLE), { ids }, { responseType: 'blob' }),
  // POST /api/v1/estimates/:id/archive | /unarchive — put an EXPIRED estimate
  // away as no longer applicable, and restore it (2026-09-29).
  archive: (id) => axiosInstance.post(stripApiPrefix(API_ENDPOINTS.ESTIMATE_ARCHIVE(id))),
  unarchive: (id) => axiosInstance.post(stripApiPrefix(API_ENDPOINTS.ESTIMATE_UNARCHIVE(id))),
  // POST /api/v1/estimates/bulk-status — one transition on a selection
  // (2026-09-29). action ∈ approve|decline|executed|invoiced|archive|unarchive;
  // `note` is required for approve/decline. Max 100 ids. The answer carries a
  // per-id `results` list and a `summary` (updated/unchanged/skipped/notFound/
  // failed): ineligible rows are skipped by the server, never forced.
  bulkStatus: (ids, action, note) =>
    axiosInstance.post(stripApiPrefix(API_ENDPOINTS.ESTIMATES_BULK_STATUS), {
      ids,
      action,
      ...(note ? { note } : {}),
    }),
};

// PUBLIC (third-party, bearer = approval token) estimate-approval methods (Phase 3, T043).
// Uses `publicAxiosInstance` (no auth interceptors, withCredentials:false) and forwards the
// approval token from `usePublicFragmentToken('estimate', uuid)` as `Authorization: Bearer`.
// Paths come ONLY from the shared API_ENDPOINTS public estimate family (bare /api prefix).
// When `token` is null/undefined the header is OMITTED (never the literal "Bearer null").
export const estimatesPublicApi = {
  // GET /api/estimates/uuid/:uuid — read the frozen quote for the approval page.
  getByUuid: (uuid, token) =>
    publicAxiosInstance.get(stripApiPrefix(API_ENDPOINTS.ESTIMATE_PUBLIC(uuid)), {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    }),
  // POST /api/estimates/uuid/:uuid/approve — { approved_by_name, approved_by_email, total_cents }.
  approve: (uuid, body, token) =>
    publicAxiosInstance.post(stripApiPrefix(API_ENDPOINTS.ESTIMATE_APPROVE(uuid)), body, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    }),
  // POST /api/estimates/uuid/:uuid/decline — { declined_reason? }.
  decline: (uuid, body, token) =>
    publicAxiosInstance.post(stripApiPrefix(API_ENDPOINTS.ESTIMATE_DECLINE(uuid)), body || {}, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    }),
  // POST /api/estimates/uuid/:uuid/delegate — { to_name, to_email }. Re-mints the
  // token server-side; this browser's link dies after a successful forward (2026-07-17).
  delegate: (uuid, body, token) =>
    publicAxiosInstance.post(stripApiPrefix(API_ENDPOINTS.ESTIMATE_DELEGATE(uuid)), body || {}, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    }),
};

// PUBLIC site alarm board — the corridor wall panel (spec 20260804-site-alarm-board).
//
// `publicAxiosInstance` ONLY. The authenticated instance would be actively wrong
// here, not merely unnecessary: its request interceptor injects a portal JWT the
// panel does not have, and its 401 handler drives a refresh queue and a session
// -expiry redirect. A corridor panel that hits a 401 must show its own code
// screen, not be bounced to /login on a device with no keyboard.
//
// TWO DIFFERENT CREDENTIALS travel through here, and mixing them up is the whole
// security model:
//   deviceToken  — long-lived, persisted in localStorage by the board device hook.
//                  Authorises `unlock` and `pendingCount`.
//   sessionToken — 15 minutes, held IN MEMORY ONLY, never persisted.
//                  Authorises `listAlarms` and `closeAlarm`.
//
// Paths come only from the shared BOARD constants — nothing is hand-typed.
export const siteBoardApi = {
  // POST /api/board/:siteUuid/unlock — { code } as a STRING. Never parseInt:
  // '098009' is not 98009 (the legacy defect at AlarmReset.jsx:86).
  unlock: (siteUuid, code, deviceToken) =>
    publicAxiosInstance.post(
      stripApiPrefix(API_ENDPOINTS.BOARD_UNLOCK(siteUuid)),
      { code: String(code) },
      { headers: deviceToken ? { Authorization: `Bearer ${deviceToken}` } : {} },
    ),

  // GET /api/board/:siteUuid/pending-count — device token alone. Returns
  // { count } and nothing else; the locked screen must render no more than that.
  // `config` carries the caller's `signal` (see timeoutSignal in @caro/shared) —
  // the panel wants a short, deterministic abort, not this instance's 30s default.
  pendingCount: (siteUuid, deviceToken, config) =>
    publicAxiosInstance.get(stripApiPrefix(API_ENDPOINTS.BOARD_PENDING_COUNT(siteUuid)), {
      ...(config || {}),
      headers: deviceToken ? { Authorization: `Bearer ${deviceToken}` } : {},
    }),

  // GET /api/board/:siteUuid/alarms — session token. Returns BoardAlarm[].
  listAlarms: (siteUuid, sessionToken, config) =>
    publicAxiosInstance.get(stripApiPrefix(API_ENDPOINTS.BOARD_ALARMS(siteUuid)), {
      ...(config || {}),
      headers: sessionToken ? { Authorization: `Bearer ${sessionToken}` } : {},
    }),

  // POST /api/board/:siteUuid/alarms/:alarmUuid/close — { status, comment }.
  // Server rejects a note under BOARD_COMMENT_MIN_LENGTH with 400.
  closeAlarm: (siteUuid, alarmUuid, body, sessionToken) =>
    publicAxiosInstance.post(
      stripApiPrefix(API_ENDPOINTS.BOARD_ALARM_CLOSE(siteUuid, alarmUuid)),
      body,
      { headers: sessionToken ? { Authorization: `Bearer ${sessionToken}` } : {} },
    ),
};

// AUTHENTICATED admin surface for the same feature (spec §4.8). Gated on
// `site_board.manage`. Note there is no `getCode` and there never will be — the
// code is bcrypt-hashed and unreadable by anyone, including an admin.
export const siteBoardAdminApi = {
  get: (siteUuid) => axiosInstance.get(`/v1/site-board/${siteUuid}`),
  setCode: (siteUuid, code) =>
    axiosInstance.put(`/v1/site-board/${siteUuid}/code`, { code: String(code) }),
  // Returns { provisioningToken } EXACTLY ONCE. The caller must surface it
  // immediately with a "will not be shown again" warning — it is unrecoverable.
  createPanel: (siteUuid, body) =>
    axiosInstance.post(`/v1/site-board/${siteUuid}/panels`, body),
  revokePanel: (siteUuid, panelId) =>
    axiosInstance.post(`/v1/site-board/${siteUuid}/panels/${panelId}/revoke`),
  clearLockout: (siteUuid) =>
    axiosInstance.post(`/v1/site-board/${siteUuid}/clear-lockout`),
};

// API client for Hexnode MDM (Mobile Device Management)
export const hexnodeApi = {
  // Device Management
  getDevices: () => axiosInstance.get('/hexnode/devices'),
  getDevice: (deviceId) => axiosInstance.get(`/hexnode/devices/${deviceId}`),
  syncDevice: (deviceId, hexnodeDeviceId) => axiosInstance.post(`/hexnode/sync/${deviceId}`, { hexnodeDeviceId }),
  importAll: () => axiosInstance.post('/hexnode/import'),
  getImportStatus: () => axiosInstance.get('/hexnode/import/status'),
  // Enrol devices from JSON copied out of the Hexnode console. Spends no
  // Hexnode requests, so it still works once the account's budget is exhausted.
  importFromJson: (json) => axiosInstance.post('/hexnode/import/json', { json }),

  // Device Actions
  restartDevice: (deviceId) => axiosInstance.post(`/hexnode/devices/${deviceId}/actions/restart`),
  clearAppData: (deviceId, appIdentifier) => axiosInstance.post(`/hexnode/devices/${deviceId}/actions/clear-data`, { appIdentifier }),
  uninstallApp: (deviceId, appId) => axiosInstance.post(`/hexnode/devices/${deviceId}/actions/uninstall`, { appId }),
  ringDevice: (deviceId, message) => axiosInstance.post(`/hexnode/devices/${deviceId}/actions/ring`, { message }),
  broadcastMessage: (deviceId, message) => axiosInstance.post(`/hexnode/devices/${deviceId}/actions/broadcast`, { message }),

  // Device Information
  getApplications: (deviceId) => axiosInstance.get(`/hexnode/devices/${deviceId}/applications`),
  getCaroApp: (deviceId) => axiosInstance.get(`/hexnode/devices/${deviceId}/caro-app`),
  getRemainingCalls: (deviceId) => axiosInstance.get(`/hexnode/devices/${deviceId}/remaining-calls`),
  getLogs: (deviceId, params) => axiosInstance.get(`/hexnode/devices/${deviceId}/logs`, { params }),
};

// API client for UniFi Network Controller (Integration API v1)
export const unifiApi = {
  // Sites Management
  getSites: () => axiosInstance.get('/unifi/sites'),

  // Site-to-Location Mappings
  getSiteMappings: () => axiosInstance.get('/unifi/site-mappings'),
  createSiteMapping: (data) => axiosInstance.post('/unifi/site-mappings', data),
  deleteSiteMapping: (id) => axiosInstance.delete(`/unifi/site-mappings/${id}`),
  getSiteMappingSuggestions: () => axiosInstance.get('/unifi/site-mappings/suggestions'),

  // Device Management
  getDevices: (params) => axiosInstance.get('/unifi/devices', { params }),
  getDeviceDetails: (deviceId) => axiosInstance.get(`/unifi/devices/${deviceId}`),
  getDeviceStatistics: (deviceId) => axiosInstance.get(`/unifi/devices/${deviceId}/statistics`),
  syncDeviceStatus: (deviceId) => axiosInstance.post(`/unifi/sync/${deviceId}`),

  // Import
  importDevices: (data) => axiosInstance.post('/unifi/import', data),
  getImportStatus: () => axiosInstance.get('/unifi/import/status'),

  // Sync (hourly cron + manual trigger)
  triggerSync: () => axiosInstance.post('/unifi/sync'),
  syncSite: (siteId) => axiosInstance.post(`/unifi/sync/site/${siteId}`),
  getSyncStatus: () => axiosInstance.get('/unifi/sync/status'),

  // Device Linking
  getLinkingStatus: (filter) => axiosInstance.get('/unifi/linking/status', { params: { filter } }),
  linkDevice: (unifiDeviceId, caroDeviceId) => axiosInstance.post('/unifi/linking/device', { unifiDeviceId, caroDeviceId }),
  linkClient: (unifiClientId, caroDeviceId) => axiosInstance.post('/unifi/linking/client', { unifiClientId, caroDeviceId }),
  unlinkDevice: (unifiDeviceId) => axiosInstance.delete(`/unifi/linking/device/${unifiDeviceId}`),
  unlinkClient: (unifiClientId) => axiosInstance.delete(`/unifi/linking/client/${unifiClientId}`),

  // Device Actions
  renameDevice: (deviceId, name) => axiosInstance.put(`/unifi/devices/${deviceId}/name`, { name }),
  restartDevice: (deviceId) => axiosInstance.post(`/unifi/devices/${deviceId}/restart`),
  forceProvision: (deviceId) => axiosInstance.post(`/unifi/devices/${deviceId}/provision`),
  upgradeDevice: (deviceId) => axiosInstance.post(`/unifi/devices/${deviceId}/upgrade`),

  // Switch Specific
  getSwitchPorts: (deviceId) => axiosInstance.get(`/unifi/devices/${deviceId}/ports`),

  // PoE Port Control (Integration API POWER_CYCLE)
  powerCyclePoEPort: (deviceId, portIdx) =>
    axiosInstance.post(`/unifi/devices/${deviceId}/ports/${portIdx}/poe/power-cycle`),

  // PoE Reboot — power cycle this device's uplink port on parent switch
  poeReboot: (deviceId) =>
    axiosInstance.post(`/unifi/devices/${deviceId}/poe-reboot`),

  // Orphaned Devices
  getOrphanedDevices: () => axiosInstance.get('/unifi/devices/orphaned'),

  // Network Topology
  getNetworkTopology: (site) => axiosInstance.get('/unifi/topology', { params: { site } }),

  // Clients
  getClients: (params) => axiosInstance.get('/unifi/clients', { params }),
  getUnlinkedClients: () => axiosInstance.get('/unifi/clients/unlinked'),

  // Pending Devices
  getPendingDevices: () => axiosInstance.get('/unifi/pending-devices'),

  // Client Search
  searchClients: (query) => axiosInstance.get('/unifi/clients/search', { params: { q: query } }),

  // Device Diagnostics
  getDeviceDiagnostics: (deviceId) => axiosInstance.get(`/unifi/devices/${deviceId}/diagnostics`),

  // Device Events
  getDeviceEvents: (deviceId, params) => axiosInstance.get(`/unifi/devices/${deviceId}/events`, { params }),

  // Correlated Offline Analysis
  getCorrelatedOffline: (deviceId) => axiosInstance.get(`/unifi/devices/${deviceId}/correlated-offline`),

  // Port Error Heatmap
  getPortHeatmap: (deviceId) => axiosInstance.get(`/unifi/devices/${deviceId}/ports/heatmap`),

  // Diagnostic Report Export
  getDiagnosticReport: (deviceId, format = 'json') =>
    axiosInstance.get(`/unifi/devices/${deviceId}/diagnostic-report`, { params: { format } }),

  // Logs
  getLogs: (params) => axiosInstance.get('/unifi/logs', { params }),
};

// API client for Clearvox VoIP Integration
export const clearvoxApi = {
  // Site Discovery
  discoverSites: () => axiosInstance.get('/admin/clearvox/discover-sites'),
  getSites: () => axiosInstance.get('/admin/clearvox/sites'),
  testConnection: (siteId) => axiosInstance.post(`/admin/clearvox/test-connection/${siteId}`),

  // Import & Sync
  importAll: () => axiosInstance.post('/admin/clearvox/import'),
  importSite: (siteId) => axiosInstance.post(`/admin/clearvox/import/${siteId}`),
  // Manually import ONE user by their Clearvox user_id — recovers users the
  // bulk sync skipped (e.g. paginated list gaps).
  importSingleUser: (siteId, clearvoxUserId) =>
    axiosInstance.post(`/admin/clearvox/import/${siteId}/user/${clearvoxUserId}`),
  syncAll: () => axiosInstance.post('/admin/clearvox/sync'),
  syncSite: (siteId) => axiosInstance.post(`/admin/clearvox/sync/${siteId}`),
  cancelSync: (siteId) => axiosInstance.post(`/admin/clearvox/cancel-sync/${siteId}`),
  getSyncStatus: () => axiosInstance.get('/admin/clearvox/sync-status'),
  getSyncStatusBySite: (siteId) => axiosInstance.get(`/admin/clearvox/sync-status/${siteId}`),

  // Statistics
  getStats: () => axiosInstance.get('/admin/clearvox/stats'),

  // Audit log filter — "Caused by Clearvox presence" (T045)
  getAuditLog: (params = {}) => axiosInstance.get('/admin/clearvox/audit', { params }),

  // Clearvox Users
  getUsers: (params) => axiosInstance.get('/clearvox/users', { params }),
  getUserById: (id) => axiosInstance.get(`/clearvox/users/${id}`),
  getUserByInternalNumber: (number) => axiosInstance.get(`/clearvox/users/by-internal/${number}`),
  getUserByExternalNumber: (number) => axiosInstance.get(`/clearvox/users/by-external/${number}`),
  linkUserToLocation: (id, locationId) => axiosInstance.post(`/clearvox/users/${id}/link-location`, { locationId }),
  unlinkUserFromLocation: (id) => axiosInstance.delete(`/clearvox/users/${id}/unlink-location`),
  syncUser: (id) => axiosInstance.post(`/clearvox/users/${id}/sync`),

  // Clearvox Devices
  getDevices: (params) => axiosInstance.get('/clearvox/devices', { params }),
  getDeviceById: (id) => axiosInstance.get(`/clearvox/devices/${id}`),
  getDeviceByUser: (userId) => axiosInstance.get(`/clearvox/devices/by-user/${userId}`),

  // Call History
  getCallHistory: (userId, params) => axiosInstance.get(`/clearvox/users/${userId}/call-history`, { params }),
  searchCallHistory: (params) => axiosInstance.get('/clearvox/call-history/search', { params }),

  // Location Linking
  getLocationUser: (locationId) => axiosInstance.get(`/clearvox/locations/${locationId}/user`),
  bulkLinkSuggestions: (data) => axiosInstance.post('/clearvox/locations/bulk-link', data),
  getUnlinkedUsers: (params) => axiosInstance.get('/clearvox/locations/unlinked-users', { params }),

  // Sync Logs
  getSyncLogs: (params) => axiosInstance.get('/admin/clearvox/sync-logs', { params }),
  getSyncLogById: (id) => axiosInstance.get(`/admin/clearvox/sync-logs/${id}`),

  // Settings
  getSettings: () => axiosInstance.get('/admin/clearvox/settings'),
  updateSettings: (settings) => axiosInstance.put('/admin/clearvox/settings', settings),
};

// API client for BaseIP Management
export const baseIPApi = {
  // Get all sites with baseIP statistics
  getSites: () => axiosInstance.get('/admin/baseip/sites'),

  // Get all units for a site with baseIP status
  getUnitsForSite: (siteId) => axiosInstance.get(`/admin/baseip/sites/${siteId}/units`),

  // Update baseIP for a single location (force=true to allow duplicates)
  updateBaseIP: (locationId, baseIP, force = false) => axiosInstance.put(`/admin/baseip/locations/${locationId}`, { baseIP, force }),

  // Bulk assign baseIPs to multiple units
  bulkAssign: (siteId, assignments) => axiosInstance.post(`/admin/baseip/sites/${siteId}/bulk`, { assignments }),

  // Get audit report
  getAuditReport: () => axiosInstance.get('/admin/baseip/audit'),
};

// API client for Telephony Management (Call Logs + SIP Sessions)
export const telephonyApi = {
  getCallLogs: (params) => axiosInstance.get('/call-logs', { params }),
  // Merged per-system call-trace timeline (asterisk + device + api steps).
  getCallEvents: (id) => axiosInstance.get(`/call-logs/${id}/events`),
  getCallLogAudioUrl: (id, type) => `${API_BASE_URL}/api/call-logs/${id}/audio${type ? `?type=${type}` : ''}`,
  getCallLogAudio: (id, type) => axiosInstance.get(`/call-logs/${id}/audio${type ? `?type=${type}` : ''}`, { responseType: 'blob' }),
  getSipSessions: (params) => axiosInstance.get('/sip-sessions', { params }),
  getSipSessionHistory: (id, params) => axiosInstance.get(`/sip-sessions/${id}/history`, { params }),
  // SIP device endpoints (2N, Grandstream, physical SIP devices)
  getSipEndpoints: (params) => axiosInstance.get('/sip-endpoints', { params }),
  getSipEndpoint: (id) => axiosInstance.get(`/sip-endpoints/${id}`),
  createSipEndpoint: (data) => axiosInstance.post('/sip-endpoints', data),
  updateSipEndpoint: (id, data) => axiosInstance.put(`/sip-endpoints/${id}`, data),
  deleteSipEndpoint: (id) => axiosInstance.delete(`/sip-endpoints/${id}`),
  regenerateSipCredentials: (id) => axiosInstance.post(`/sip-endpoints/${id}/regenerate-credentials`),
  getSipEndpointStatus: (id) => axiosInstance.get(`/sip-endpoints/${id}/status`),
  reloadAsterisk: () => axiosInstance.post('/sip-endpoints/reload-asterisk'),
  provisionLocationSipDevice: (data) => axiosInstance.post('/sip-endpoints/provision-location', data),
  provisionPersonSipDevice: (data) => axiosInstance.post('/sip-endpoints/provision-person', data),
  syncCameraPaths: () => axiosInstance.post('/sip-endpoints/sync-camera-paths'),
  // Audio device configuration
  getAudioConfig: (id) => axiosInstance.get(`/sip-endpoints/${id}/audio`),
  updateAudioConfig: (id, config) => axiosInstance.put(`/sip-endpoints/${id}/audio`, config),
  refreshAudioDevices: (id) => axiosInstance.post(`/sip-endpoints/${id}/audio/refresh`),
};

// API client for the resident-tablet permissions overview (camera/mic/etc).
export const tabletPermissionsApi = {
  // params: { siteId?, locationId? } → { success, count, devices: [...] }
  getOverview: (params) => axiosInstance.get('/residentApp/admin/tablet-permissions', { params }),
};

// API client for DID Mappings (external phone number → internal extension routing)
export const didMappingApi = {
  getAll: (params) => axiosInstance.get('/didMappings', { params }),
  getById: (id) => axiosInstance.get(`/didMappings/${id}`),
  create: (data) => axiosInstance.post('/didMappings', data),
  update: (id, data) => axiosInstance.put(`/didMappings/${id}`, data),
  delete: (id) => axiosInstance.delete(`/didMappings/${id}`),
};

// API client for persons
export const personsApi = {
  // Get all persons
  getAll: (params) => axiosInstance.get('/persons', {
    params: { limit: 5000, ...params }
  }),

  // Alias for getAll
  getPersons: (params) => axiosInstance.get('/persons', {
    params: { limit: 5000, ...params }
  }),

  // Get specific person by ID
  getById: (id) => axiosInstance.get(`/persons/${id}`),

  // Create new person
  create: (personData) => axiosInstance.post('/persons', personData),

  // Update existing person
  update: (id, personData) => axiosInstance.put(`/persons/${id}`, personData),

  // Delete person
  delete: (id) => axiosInstance.delete(`/persons/${id}`),

  // Bulk delete persons
  // bulkDelete is VERWIJDERD (T-7.17, offboarding.md LD-11). De route bestaat niet meer
  // op de server; wie bewoners wil laten vertrekken, gebruikt /persons/offboard --
  // dan blijft de historiek staan en blijft facturatie mogelijk.

  // Get person folder structure
  getFolders: (id) => axiosInstance.get(`/persons/${id}/folders`),

  // Get persons by location
  getByLocation: (locationId) => axiosInstance.get('/persons', {
    params: { personLocation: locationId }
  }),

  // Invite person to become a user
  sendInvite: (id, data) => axiosInstance.post(`/persons/${id}/invite`, data),

  // Resend invite email
  resendInvite: (id) => axiosInstance.post(`/persons/${id}/resend-invite`),

  // Cancel pending invite
  cancelInvite: (id) => axiosInstance.delete(`/persons/${id}/invite`),

  // Get linked user info
  getLinkedUser: (id) => axiosInstance.get(`/persons/${id}/user`),
};

// API client for plaatsbeschrijvingen (paid module `inspections`).
//
// Reads use requireModuleHistory server-side, so a customer who stops paying keeps every
// report they made and can still export it — writes are refused. That asymmetry is not
// expressed here: the server decides, and this client just calls.
export const inspectionsApi = {
  list: (params) => axiosInstance.get('/inspections', { params }),

  // AUDITED read — a published report contains photographs of a named resident's home.
  getByUuid: (uuid) => axiosInstance.get(`/inspections/${uuid}`),

  // Snapshots the template at creation. Refuses NOT_A_UNIT for a site or building, and
  // PLAN_REQUIRED for the first report of a flat that has no floor plan.
  create: (body) => axiosInstance.post('/inspections', body),

  // Computed on read, never stored — a stored diff would need invalidating every time
  // either side is corrected.
  comparison: (uuid) => axiosInstance.get(`/inspections/${uuid}/comparison`),

  updateRoom: (uuid, roomUuid, body) =>
    axiosInstance.patch(`/inspections/${uuid}/rooms/${roomUuid}`, body),
  updateItem: (uuid, itemUuid, body) =>
    axiosInstance.patch(`/inspections/${uuid}/items/${itemUuid}`, body),
  // Removing an inapplicable item deletes the report's COPY, never the template.
  removeItem: (uuid, itemUuid) =>
    axiosInstance.delete(`/inspections/${uuid}/items/${itemUuid}`),

  addPin: (uuid, body) => axiosInstance.post(`/inspections/${uuid}/pins`, body),
  addMeterReading: (uuid, body) => axiosInstance.post(`/inspections/${uuid}/meter-readings`, body),

  // ── The three signature routes ────────────────────────────────────────────
  // (a) on the device: the LAST required signature publishes in the same transaction.
  addSignature: (uuid, body) => axiosInstance.post(`/inspections/${uuid}/signatures`, body),
  // (b) online: the only route with an in_review state and a confirmation mail.
  sendForReview: (uuid) => axiosInstance.post(`/inspections/${uuid}/send-for-review`),
  remind: (uuid) => axiosInstance.post(`/inspections/${uuid}/remind`),
  // (c) paper: the scan, the assertion and the status change are ONE action.
  publishWithScan: (uuid, body) => axiosInstance.post(`/inspections/${uuid}/publish-with-scan`, body),

  recordRefusal: (uuid, body) => axiosInstance.post(`/inspections/${uuid}/refusal`, body),
  publishWithoutConfirmation: (uuid, body) =>
    axiosInstance.post(`/inspections/${uuid}/publish-without-confirmation`, body),
  // A correction is a NEW report referencing the published one. Never an edit.
  createCorrection: (uuid) => axiosInstance.post(`/inspections/${uuid}/correction`),

  pdfUrl: (uuid) => `/api/inspections/${uuid}/pdf`,

  // ══ De plattegrond van het appartement ════════════════════════════════════
  //
  // Hij hoort bij het APPARTEMENT en niet bij een rapport: het eerste rapport laadt hem op,
  // elk volgend rapport hergebruikt hem, en hij overleeft ze allemaal. Vandaar de mount
  // onder de locatie.

  /** Wat er al ligt. `pinCount` is de waarschuwing vóór een vervanging. */
  floorPlans: (locationId, params) =>
    axiosInstance.get(`/units/${locationId}/floor-plans`, { params }),

  /**
   * Stap 1 — het bestand zelf.
   *
   * `FormData` met veld `file`; de Content-Type wordt BEWUST niet gezet, zodat de browser
   * zelf de multipart-boundary invult. Handmatig `multipart/form-data` zetten laat die
   * boundary weg en dan ziet de server een leeg bestand.
   *
   * ⚠ Bij een PDF van meer dan één pagina antwoordt dit met **409
   * `PLAN_PAGE_SELECTION_REQUIRED`**. Dat is een VRAAG, geen fout: het antwoord draagt
   * `sourceFileId`, `pageCount` en de gemeten afmetingen per pagina, en de volgende stap is
   * `completeFloorPlanFromSource`. Wie de 409 als foutmelding toont, laat de gebruiker
   * denken dat zijn scan geweigerd is.
   */
  intakeFloorPlan: (locationId, formData) =>
    axiosInstance.post(`/units/${locationId}/floor-plans/intake`, formData),

  /** Stap 2 — welke pagina is welk niveau. */
  completeFloorPlanFromSource: (locationId, body) =>
    axiosInstance.post(`/units/${locationId}/floor-plans/from-source`, body),

  /**
   * Een duimnagel van pagina N van de bewaarde bronscan.
   *
   * Zonder dit is "kies pagina 1 t/m 12" geen keuze maar een gok — precies wat we de server
   * verboden hebben. Blob, want de route vraagt een Authorization-header en een `<img src>`
   * stuurt die niet mee.
   */
  floorPlanPagePreview: (locationId, sourceFileId, page) =>
    axiosInstance.get(
      `/units/${locationId}/floor-plans/source/${sourceFileId}/pages/${page}/preview`,
      { responseType: 'blob' },
    ),

  /** Het plan zelf. Ook blob, om dezelfde reden. */
  floorPlanImage: (locationId, planUuid) =>
    axiosInstance.get(`/units/${locationId}/floor-plans/${planUuid}/image`, {
      responseType: 'blob',
    }),

  /**
   * De REGISTRATIE-route: een bestand dat al opgeslagen en gemeten is.
   *
   * ⚠ Deze neemt `imageWidthPx`/`imageHeightPx` van de beller aan. Voor een echte upload is
   * `intakeFloorPlan` de weg; die meet de bytes zelf.
   */
  addFloorPlan: (locationId, body) =>
    axiosInstance.post(`/units/${locationId}/floor-plans`, body),
};

// API client for a resident's contact persons — "who do we ring".
//
// Nested under the resident because a contact has no independent existence
// (resident_contacts.resident_person_id is NOT NULL), and because the server resolves the
// PARENT PERSON through the tenant gate before it will touch a contact at all: a contact's
// tenant is three joins deep. There is deliberately no /contacts/:id top-level route to
// call.
//
// No new permission key: reads are persons.view, writes are persons.edit.
export const residentContactsApi = {
  // includeInactive=true also returns deactivated rows (they are never deleted).
  list: (personId, { includeInactive = false } = {}) =>
    axiosInstance.get(`/persons/${personId}/contacts`, { params: { includeInactive } }),

  create: (personId, contact) => axiosInstance.post(`/persons/${personId}/contacts`, contact),

  update: (personId, contactId, contact) =>
    axiosInstance.put(`/persons/${personId}/contacts/${contactId}`, contact),

  // DEACTIVATES. The row survives and comes back with includeInactive.
  deactivate: (personId, contactId) =>
    axiosInstance.delete(`/persons/${personId}/contacts/${contactId}`),

  // The order is WHO IS CONTACTED FIRST, not a screen preference. The server rewrites
  // display_order dense and 1-based, and refuses a partial list rather than renumbering
  // half a call-out order.
  reorder: (personId, contactIds) =>
    axiosInstance.put(`/persons/${personId}/contacts/order`, { contactIds }),
};

// API client for user home buttons (per-person home screen config)
export const homeButtonsApi = {
  // Get buttons for a person (by linked userId)
  getByPerson: (personId) => axiosInstance.get(`/persons/${personId}/home-buttons`),

  // Save/replace all buttons for a person
  saveForPerson: (personId, buttons) => axiosInstance.put(`/persons/${personId}/home-buttons`, { buttons }),

  // Reset to default buttons
  resetForPerson: (personId) => axiosInstance.delete(`/persons/${personId}/home-buttons`),
};

// API client for person types
/**
 * Residenties — de PERIODE dat iemand in een flat woont.
 *
 * ⚠ Dit is de enige weg om `persons.personLocation` te laten wijzigen. Die kolom is sinds
 * fase 6 AFGELEID: het model zegt "written only by residencyService". Hem rechtstreeks
 * zetten via het bewonersformulier zou een tweede schrijver maken, en dan staat er een
 * bewoner in een flat zonder residentie — zonder startdatum, buiten de facturatie die op
 * die periode rekent, en met niets voor de uittrede om af te sluiten.
 */
export const residenciesApi = {
  list: (params) => axiosInstance.get('/residencies', { params }),

  /** Opent een residentie en schrijft daarmee de afgeleide pointer. `persons.create`. */
  create: ({ personId, unitLocationId, startedOn }) =>
    axiosInstance.post('/residencies', { personId, unitLocationId, startedOn }),
};

export const personTypesApi = {
  // Get all person types
  getAll: (params) => axiosInstance.get('/persontypes', { params }),

  // Get specific person type by ID
  getById: (id) => axiosInstance.get(`/persontypes/${id}`),

  // Create new person type
  create: (data) => axiosInstance.post('/persontypes', data),

  // Update existing person type
  update: (id, data) => axiosInstance.put(`/persontypes/${id}`, data),

  // Delete person type
  delete: (id) => axiosInstance.delete(`/persontypes/${id}`),

  // Bulk delete person types
  bulkDelete: (ids) => axiosInstance.post('/persontypes/bulk-delete', { ids }),
};

// API client for firmware versions
export const firmwareVersionsApi = {
  // Get all firmware versions with optional filters
  getAll: (params) => axiosInstance.get('/firmware-versions', { params }),

  // Get firmware version by ID
  getById: (id) => axiosInstance.get(`/firmware-versions/${id}`),

  // Get firmware versions by device type
  getByDeviceType: (deviceTypeId) => axiosInstance.get('/firmware-versions', {
    params: { device_type: deviceTypeId, limit: 1000 }
  }),

  // Device count per version, fleet-wide — the Devices column on /firmware-management.
  //
  // Spec: specs/20260825-overzicht-per-firmwareversie §5.
  //
  // Lives under /devices rather than /firmwareversions because the numbers are counts
  // of DEVICES; the catalog is only what they are bucketed against. Params:
  // { companyId, siteId, deviceType }, all optional.
  //
  // The response carries a `link` object on every row — the exact query params that
  // reproduce that number on /devices. Build the deep link from it rather than
  // assembling params here: a second spelling of the filter in this file is how the
  // count and the list start disagreeing (LD-7).
  //
  // CARO-only (company 1), enforced server-side as well as by the route guard.
  getVersionRollup: (params) => axiosInstance.get('/devices/version-rollup', { params }),

  // Create new firmware version
  create: (data) => axiosInstance.post('/firmware-versions', data),

  // Update firmware version
  update: (id, data) => axiosInstance.put(`/firmware-versions/${id}`, data),

  // Delete firmware version
  delete: (id) => axiosInstance.delete(`/firmware-versions/${id}`),

  // Bulk delete
  bulkDelete: (ids) => axiosInstance.post('/firmware-versions/bulk-delete', { ids }),
};

// API client for firmware update runs — the push behind the Devices column.
//
// Spec: specs/20260826-firmware-update-push/spec.md
//
// A run walks each device up the caro-agent update chain one version at a time
// and records what arrived at the API per step. It is SERVER-SIDE on purpose:
// one `update` command is worth exactly one version step (every update.sh
// restarts the agent, which kills the loop inside check_and_update), so bringing
// the measured fleet to the latest version is ~894 steps — hours of work that
// must survive a closed tab.
//
// `create` takes the SELECTION, not a device list: versionKind + version or
// versionBucket, plus the page's active companyId/siteId/deviceType. The server
// resolves it with the same matcher that produced the number, so the count that
// was clicked and the devices in the run cannot drift apart.
//
// CARO-only (company 1) and `devices.admin`, both enforced server-side.
export const firmwareUpdateRunsApi = {
  // { versionKind, version | versionBucket, serviceName, companyId, siteId,
  //   deviceType, deviceIds, concurrency, stepTimeoutSeconds, dryRun }
  //
  // Send `dryRun: true` first to get the target list and the per-device skip
  // reasons without starting anything.
  create: (data) => axiosInstance.post('/devices/firmware-update-runs', data),

  list: (params) => axiosInstance.get('/devices/firmware-update-runs', { params }),

  // Pass `sinceEventId` to fetch only what is new — the dialog polls this every
  // couple of seconds and appends, rather than re-reading the whole log.
  get: (uuid, params) => axiosInstance.get(`/devices/firmware-update-runs/${uuid}`, { params }),

  // Cooperative: a step already in flight is allowed to finish. Cutting off a
  // device mid-install is more dangerous than letting it land.
  cancel: (uuid) => axiosInstance.post(`/devices/firmware-update-runs/${uuid}/cancel`),

  // A run that outlived its driver (API restart) comes back as `paused`.
  resume: (uuid) => axiosInstance.post(`/devices/firmware-update-runs/${uuid}/resume`),
};

// API client for Emergency Service Integrations
export const emergencyServicesApi = {
  // Statistics
  getStats: () => axiosInstance.get('/emergency-services/stats'),

  // Providers
  getProviders: (params) => axiosInstance.get('/emergency-services/providers', { params }),
  getProviderById: (id) => axiosInstance.get(`/emergency-services/providers/${id}`),
  createProvider: (data) => axiosInstance.post('/emergency-services/providers', data),
  updateProvider: (id, data) => axiosInstance.put(`/emergency-services/providers/${id}`, data),
  deleteProvider: (id) => axiosInstance.delete(`/emergency-services/providers/${id}`),

  // Integrations
  getIntegrations: (params) => axiosInstance.get('/emergency-services/integrations', { params }),
  getIntegrationById: (id) => axiosInstance.get(`/emergency-services/integrations/${id}`),
  getIntegrationsByLocation: (locationId) => axiosInstance.get(`/emergency-services/location/${locationId}/integrations`),
  createIntegration: (data) => axiosInstance.post('/emergency-services/integrations', data),
  updateIntegration: (id, data) => axiosInstance.put(`/emergency-services/integrations/${id}`, data),
  deleteIntegration: (id) => axiosInstance.delete(`/emergency-services/integrations/${id}`),
  testIntegration: (id) => axiosInstance.post(`/emergency-services/integrations/${id}/test`),

  // Alarm Types (for mapping)
  getAlarmTypes: () => axiosInstance.get('/emergency-services/alarm-types'),

  // Available Sites (for integration dialog)
  getSites: (params) => axiosInstance.get('/emergency-services/sites', { params }),
};

// API client for Alarms
export const alarmsApi = {
  // Active alarms
  getActiveAlarms: () => axiosInstance.get('/alarms/active'),

  // Alarm history
  getAlarmHistory: (params) => axiosInstance.get('/alarms/history', { params }),

  // Get alarm by ID (authenticated)
  getAlarmById: (id) => axiosInstance.get(`/alarms/${id}`),

  // Get alarm by UUID (public - bearer-token guarded).
  // Forwards `Authorization: Bearer <token>` when a token is provided
  // (recommendation per plan §3.1). Header convention chosen to match
  // backend middleware/auth.js (passport-jwt fromAuthHeaderAsBearerToken).
  // Spec: specs/20260428-secure-public-alarm-access/plan.md §5.1 step 5.
  getAlarmByUUID: (uuid, token) =>
    publicAxiosInstance.get(`/alarms/uuid/${uuid}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    }),

  // Update alarm status (authenticated)
  // comment is required for terminal states (finished, cancelled)
  updateAlarmStatus: (id, status, comment = null) => axiosInstance.post(`/alarms/${id}/status`, { status, comment }),

  // Update alarm status by UUID (public - bearer-token guarded).
  // Same Authorization header pattern as getAlarmByUUID. When `token` is
  // null/undefined the header is OMITTED (not the literal "Bearer null").
  updateAlarmStatusByUUID: (uuid, status, comment = null, token) =>
    publicAxiosInstance.post(`/alarms/uuid/${uuid}/status`, { status, comment }, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    }),

  // CRUD operations
  getAll: (params) => axiosInstance.get('/alarms', { params }),
  create: (data) => axiosInstance.post('/alarms', data),
  update: (id, data) => axiosInstance.put(`/alarms/${id}`, data),
  delete: (id) => axiosInstance.delete(`/alarms/${id}`),

  // Phase D — T012: Resolve unit-type ancestor for an alarm.
  // Returns `{ id, uuid, friendlyName, resolvedFrom, walkedFrom }` on 200,
  // `null` on 204 (no unit ancestor found in the chain).
  // The backend self-heals: on first call it persists the result into
  // `Content.alarmContext.unitLocationOfAlarm` so subsequent calls are
  // O(1) lookups. Second portal mount can read directly from the alarm.
  getUnitLocation: async (id) => {
    try {
      const res = await axiosInstance.get(`/alarms/${id}/unit-location`);
      // 204 No Content — the alarm has no unit ancestor. Axios returns
      // `data` === '' (empty string) for 204. Normalize to `null`.
      if (res.status === 204 || res.data === '' || res.data == null) {
        return null;
      }
      return res.data;
    } catch (err) {
      // 404 = alarm not found. Don't swallow other errors (network, 5xx).
      if (err?.response?.status === 404) return null;
      throw err;
    }
  },
};

// API client for Alarm States (lookup — read-only)
// Phase F — T025: used by the Automation editor's Internal-Event dropdown
// to populate per-state options from the authoritative `alarm_states` table.
// Returns a plain array of { id, code, friendlyName, sortOrder, isTerminal, color }
// sorted by `sortOrder ASC, id ASC`.
export const alarmStatesApi = {
  list: () => axiosInstance.get('/alarm-states'),
};

// Read-only lookup for `alarm_types` — used by TriggerAlarmConfig dropdown
// and InternalEventConfig alarm-type filter. Plain array, sorted by id ASC.
export const alarmTypesApi = {
  list: () => axiosInstance.get('/alarm-types'),
};

// API client for button tests
export const buttonTestsApi = {
  // Start a new test session
  startTest: (data) => axiosInstance.post('/button-tests/start', data),

  // Get test session status
  getTestStatus: (testUUID) => axiosInstance.get(`/button-tests/${testUUID}`),

  // End a test session
  endTest: (testUUID, data) => axiosInstance.post(`/button-tests/${testUUID}/end`, data),

  // Skip a button in the test (reason is mandatory, min 3 characters)
  skipButton: (testUUID, resultId, data) => axiosInstance.post(`/button-tests/${testUUID}/skip/${resultId}`, data),

  // Update button name during test
  updateButtonName: (testUUID, resultId, friendlyName) =>
    axiosInstance.put(`/button-tests/${testUUID}/button/${resultId}/name`, { friendlyName }),

  // Update button room (for fixed buttons that must be linked to a room)
  updateButtonRoom: (testUUID, resultId, roomId) =>
    axiosInstance.put(`/button-tests/${testUUID}/button/${resultId}/room`, { roomId }),

  // Get naming suggestions (rooms and persons)
  getNamingSuggestions: (testUUID) => axiosInstance.get(`/button-tests/${testUUID}/naming-suggestions`),

  // Get test history for a location
  getTestHistory: (locationId, params) => axiosInstance.get(`/button-tests/history/${locationId}`, { params }),

  // Get test report for download
  getTestReport: (testUUID, format = 'json') => {
    const config = format === 'csv'
      ? { params: { format: 'csv' }, responseType: 'text' }
      : { params: { format: 'json' } };
    return axiosInstance.get(`/button-tests/${testUUID}/report`, config);
  },
};

// API client for update paths
export const updatePathsApi = {
  // Get all update paths with optional filters
  getAll: (params) => axiosInstance.get('/update-paths', { params }),

  // Get update path by ID
  getById: (id) => axiosInstance.get(`/update-paths/${id}`),

  // Get update paths by device type
  getByDeviceType: (deviceTypeId) => axiosInstance.get('/update-paths', {
    params: { update_path_device_type: deviceTypeId, limit: 1000 }
  }),

  // Create new update path
  create: (data) => axiosInstance.post('/update-paths', data),

  // Update update path
  update: (id, data) => axiosInstance.put(`/update-paths/${id}`, data),

  // Delete update path
  delete: (id) => axiosInstance.delete(`/update-paths/${id}`),

  // Bulk delete
  bulkDelete: (ids) => axiosInstance.post('/update-paths/bulk-delete', { ids }),
};

// API client for services
export const servicesApi = {
  // Get all services with optional filters
  getAll: (params) => axiosInstance.get('/services', { params }),

  // Get service by ID
  getById: (id) => axiosInstance.get(`/services/${id}`),

  // Create new service
  create: (data) => axiosInstance.post('/services', data),

  // Update service
  update: (id, data) => axiosInstance.put(`/services/${id}`, data),

  // Delete service
  delete: (id) => axiosInstance.delete(`/services/${id}`),

  // Versions
  getVersions: (serviceId) => axiosInstance.get(`/services/${serviceId}/versions`),
  createVersion: (serviceId, data) => axiosInstance.post(`/services/${serviceId}/versions`, data),
  updateVersion: (id, data) => axiosInstance.put(`/services/versions/${id}`, data),
  deleteVersion: (id) => axiosInstance.delete(`/services/versions/${id}`),

  // Update Paths
  getUpdatePaths: (serviceId) => axiosInstance.get(`/services/${serviceId}/update-paths`),
  createUpdatePath: (serviceId, data) => axiosInstance.post(`/services/${serviceId}/update-paths`, data),
  updateUpdatePath: (id, data) => axiosInstance.put(`/services/update-paths/${id}`, data),
  deleteUpdatePath: (id) => axiosInstance.delete(`/services/update-paths/${id}`),
};

// API client for device types
export const deviceTypesApi = {
  // Get all device types with optional filters
  getAll: (params) => axiosInstance.get('/deviceTypes', { params }),

  // Get device type by ID
  getById: (id) => axiosInstance.get(`/deviceTypes/${id}`),

  // Create new device type
  create: (data) => axiosInstance.post('/deviceTypes', data),

  // Update device type
  update: (id, data) => axiosInstance.put(`/deviceTypes/${id}`, data),

  // Delete device type
  delete: (id) => axiosInstance.delete(`/deviceTypes/${id}`),

  // Get services for a device type
  getServices: (id) => axiosInstance.get(`/deviceTypes/${id}/services`),

  // Update services for a device type
  updateServices: (id, data) => axiosInstance.put(`/deviceTypes/${id}/services`, data),
};

// API client for Qbus domotics integration
export const qbusApi = {
  // Output Types
  getOutputTypes: () => axiosInstance.get('/qbus/output-types'),

  // Controllers
  getControllersForLocation: (locationId) => axiosInstance.get(`/qbus/locations/${locationId}/controllers`),

  // Outputs
  getOutputsForController: (controllerId) => axiosInstance.get(`/qbus/controllers/${controllerId}/outputs`),
  getOutputsForLocation: (locationId, params) => axiosInstance.get(`/qbus/locations/${locationId}/outputs`, { params }),
  updateOutput: (outputId, data) => axiosInstance.patch(`/qbus/outputs/${outputId}`, data),
  bulkAssignOutputType: (outputIds, outputTypeId) => axiosInstance.patch('/qbus/outputs/bulk-assign-type', { outputIds, outputTypeId }),
  getUniqueOutputNames: (locationId) => axiosInstance.get(`/qbus/locations/${locationId}/unique-names`),
  bulkUpdateFriendlyNames: (locationId, mappings) => axiosInstance.patch('/qbus/outputs/bulk-friendly-names', { locationId, mappings }),
  getUniqueOutputNamesGlobal: () => axiosInstance.get('/qbus/unique-names'),

  // Name-prefix classification (spec 20260826-domotica-via-resident-app §4.2).
  // The overview is the worklist: which name buckets exist, how many outputs each
  // holds, and whether anyone has decided about them. Unreviewed sorts first.
  getPrefixOverview: () => axiosInstance.get('/qbus/prefixes'),
  createPrefixRule: (data) => axiosInstance.post('/qbus/prefixes', data),
  updatePrefixRule: (id, data) => axiosInstance.put(`/qbus/prefixes/${id}`, data),
  // `dryRun` reports the counts without writing. The real run only ever touches rows
  // whose flag is still NULL, so a manual decision is never overwritten.
  // `force` noemt PER VELD wat er overschreven mag worden bij reeds beoordeelde
  // uitgangen. Zonder dat vult Toepassen alleen wat nog NULL is, en heeft het
  // heroverwegen van een bucket geen enkel effect op de bestaande rijen.
  reseedPrefixFlags: (dryRun = false, force = []) =>
    axiosInstance.post('/qbus/prefixes/reseed', { dryRun, force }),

  // ── Standaardsferen ──────────────────────────────────────────────────────
  //
  // Sferen die in ELK appartement van een site verschijnen en daar de EIGEN lampen
  // bedienen. Hun leden staan op NAAM (VERL_WOONKAMER), niet op id — met id's zou
  // dezelfde knop overal de lampen van één appartement schakelen.
  getSceneTemplates: (siteId) => axiosInstance.get('/qbus/scene-templates', { params: { siteId } }),
  createSceneTemplate: (data) => axiosInstance.post('/qbus/scene-templates', data),
  updateSceneTemplate: (id, data) => axiosInstance.put(`/qbus/scene-templates/${id}`, data),
  deleteSceneTemplate: (id) => axiosInstance.delete(`/qbus/scene-templates/${id}`),
  // Distinct raw QbusOutput.name values for the Automation block-config
  // autocomplete (spec: 20260422-qbus-general-flow-block). When locationId
  // is passed, results are scoped to the enclosing unit (locationType=4).
  getOutputNames: (locationId) => axiosInstance.get('/qbus/output-names', {
    params: locationId != null && locationId !== '' ? { locationId } : {},
  }),
  bulkUpdateFriendlyNamesGlobal: (mappings) => axiosInstance.patch('/qbus/outputs/bulk-friendly-names-global', { mappings }),

  // Output Commands (fire-and-forget)
  sendOutputCommand: (outputId, value) => axiosInstance.post(`/qbus/outputs/${outputId}/command`, { value }),

  // Output History
  getOutputHistory: (outputId, params) => axiosInstance.get(`/qbus/outputs/${outputId}/history`, { params }),

  // Hub Management
  refreshHubConfig: (hubId) => axiosInstance.post(`/qbus/hubs/${hubId}/refresh-config`),
  getHubStatus: (hubId) => axiosInstance.get(`/qbus/hubs/${hubId}/status`),

  // Live monitor (qbus heartbeat + retained health + state/setState event ring).
  // Server-side store is in-memory; on first request after a server restart it
  // may be empty until heartbeats arrive (~60s).
  monitor: {
    listHubs: () => axiosInstance.get('/qbus/monitor/hubs'),
    getHubDetail: (mac) => axiosInstance.get(`/qbus/monitor/hubs/${encodeURIComponent(mac)}`),
    // Manual round-trip test: fires setState and waits up to `timeoutMs`
    // for the matching state event to come back. Returns timing.
    runRoundTrip: (body) => axiosInstance.post('/qbus/monitor/test/round-trip', body),
    // Operator hub commands (refresh-config, rearm-all, dump-state).
    sendCmd: (mac, verb) => axiosInstance.post(`/qbus/monitor/hubs/${encodeURIComponent(mac)}/cmd`, { verb }),
    // Recent setState round-trip samples for histogram.
    getRoundTrips: (mac) => axiosInstance.get(`/qbus/monitor/hubs/${encodeURIComponent(mac)}/round-trips`),
    // Filtered live stream — server applies post-hoc filters to the
    // events ring (kinds, controllerUlId, outputUlId, sinceMs, locationId).
    // Same shape as getHubDetail with an extra .filtersApplied summary.
    getHubStream: (mac, params) => axiosInstance.get(
      `/qbus/monitor/hubs/${encodeURIComponent(mac)}/stream`,
      { params: params || {} }
    ),
    // DB-backed recent automation execution history with heuristic
    // duplicate clusters. hubMac/outputId are accepted but ignored
    // server-side until trace_id ships on executions.
    getRecentRuns: (params) => axiosInstance.get(
      '/qbus/monitor/recent-runs',
      { params: params || {} }
    ),
    // Lightweight {locations, devices} for the FilterBar typeaheads.
    // No pagination — returns everything the user has access to.
    getFilterOptions: () => axiosInstance.get('/qbus/monitor/filter-options'),
  },

  // Icon Mappings
  getIconMappings: () => axiosInstance.get('/qbus/icon-mappings'),
  createIconMapping: (data) => axiosInstance.post('/qbus/icon-mappings', data),
  updateIconMapping: (id, data) => axiosInstance.put(`/qbus/icon-mappings/${id}`, data),
  deleteIconMapping: (id) => axiosInstance.delete(`/qbus/icon-mappings/${id}`),

  // Folders
  getFolders: (locationId) => axiosInstance.get(`/qbus/locations/${locationId}/folders`),
  createFolder: (locationId, data) => axiosInstance.post(`/qbus/locations/${locationId}/folders`, data),
  updateFolder: (folderId, data) => axiosInstance.patch(`/qbus/folders/${folderId}`, data),
  deleteFolder: (folderId) => axiosInstance.delete(`/qbus/folders/${folderId}`),
  reorderFolders: (locationId, folderIds) => axiosInstance.put(`/qbus/locations/${locationId}/folders/reorder`, { folderIds }),
  assignOutputsToFolder: (outputIds, folderId) => axiosInstance.patch('/qbus/outputs/assign-folder', { outputIds, folderId }),

  // Automations
  getAutomations: (locationId, params) => axiosInstance.get(`/qbusAutomations/locations/${locationId}`, { params }),
  getSystemAutomations: (params) => axiosInstance.get('/qbusAutomations/system', { params }),
  getAutomation: (id) => axiosInstance.get(`/qbusAutomations/${id}`),
  createAutomation: (locationId, data) => axiosInstance.post(`/qbusAutomations/locations/${locationId}`, data),
  createSystemAutomation: (data) => axiosInstance.post('/qbusAutomations/system', data),
  updateAutomation: (id, data) => axiosInstance.put(`/qbusAutomations/${id}`, data),
  deleteAutomation: (id) => axiosInstance.delete(`/qbusAutomations/${id}`),
  toggleAutomation: (id) => axiosInstance.patch(`/qbusAutomations/${id}/toggle`),
  testAutomation: (id, triggerData) => axiosInstance.post(`/qbusAutomations/${id}/test`, { triggerData }),
  getAutomationExecutions: (id, params) => axiosInstance.get(`/qbusAutomations/${id}/executions`, { params }),
  getExecutionDetail: (executionId) => axiosInstance.get(`/qbusAutomations/executions/${executionId}`),
  cancelExecution: (executionId) => axiosInstance.post(`/qbusAutomations/executions/${executionId}/cancel`),
  getBlockTypes: () => axiosInstance.get('/qbusAutomations/block-types'),
  replayExecution: (automationId, executionId) => axiosInstance.post(`/qbusAutomations/${automationId}/replay/${executionId}`),
  // Ticket reminders (spec 20260930-ticketherinneringen-automations §5). CARO-only on the
  // server: everyone else gets a 404.
  getTicketReminderTemplates: () =>
    axiosInstance.get('/qbusAutomations/templates', { params: { kind: 'ticket_reminder' } }),
  previewTicketStaleUnsaved: (body) => axiosInstance.post('/qbusAutomations/ticket-stale/preview', body),
  previewTicketStale: (id) => axiosInstance.post(`/qbusAutomations/${id}/ticket-stale/preview`),
  getTicketReminders: (id, { ticketId, page, pageSize, sort, order } = {}) =>
    axiosInstance.get(`/qbusAutomations/${id}/ticket-reminders`, {
      params: { ticketId, page, pageSize, sort, order },
    }),
};

// ── Observability feedback loop (spec 20260807-observability-feedback-loop §7) ──
//
// ⚠ NOT `createApiClient`, for the same reason recorded on `ideasApi` below: CLAUDE.md claims
// both apps use it, and the portal does not. Its own `axiosInstance` (:15) is the client here.
//
// What this surface is FOR: showing whether a fix worked. The count series per finding is the
// only thing here that cannot be reconstructed, and the agent on VM5 owns it — these endpoints
// read a copy. The one write is `markFixShipped`, which is the single transition a human owns.
export const observabilityApi = {
  // GET /api/observability/runs — run history. Feeds the cost tile.
  listRuns: (params = {}) =>
    axiosInstance.get(stripApiPrefix(API_ENDPOINTS.OBSERVABILITY_RUNS), { params }),

  // GET /api/observability/findings?status=&source=&trend=
  //
  // `trend` is how many recent runs of count history to return per finding (default 8). The
  // server returns `trend: [n, n, null, …]` oldest→newest, where **null means the finding was
  // absent from that run** — not zero. That distinction is the whole point: an absence after a
  // fix shipped is what promotes a finding to `resolved`.
  //
  // No client-side limit is imposed here. A hardcoded one on top of a server default is how this
  // portal has previously shown a partial list while looking complete.
  listFindings: (params = {}) => {
    const query = {};
    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '' || value === 'all') return;
      query[key] = value;
    });
    return axiosInstance.get(stripApiPrefix(API_ENDPOINTS.OBSERVABILITY_FINDINGS), { params: query });
  },

  // GET /api/observability/findings/:id — one finding with its FULL count series and the run
  // each count came from, so a trend can be read against the window that produced it.
  getFinding: (id) =>
    axiosInstance.get(stripApiPrefix(API_ENDPOINTS.OBSERVABILITY_FINDING(id))),

  // GET /api/observability/cost?months=6 — per-month totals.
  // Read `runsWithUnknownCost` and `runsWithPartialCost` alongside the amount: a run whose
  // analysis was killed before reporting its spend makes the month's total a FLOOR, and showing
  // a floor as a total is exactly what this feature exists to prevent.
  getCost: (params = {}) =>
    axiosInstance.get(stripApiPrefix(API_ENDPOINTS.OBSERVABILITY_COST), { params }),

  // POST /api/observability/findings/:id/fix-shipped — { note, at? }
  //
  // Requires `monitoring.manage`, not `monitoring.view`. The server rejects an empty note with
  // 400: a claim that a fix shipped, with nothing pointing at a commit or deploy, is not evidence.
  // This does NOT close the finding — it starts a measurement.
  markFixShipped: (id, data) =>
    axiosInstance.post(stripApiPrefix(API_ENDPOINTS.OBSERVABILITY_FINDING_FIX_SHIPPED(id)), data),
};

// API client for family photo links (Tier 1 — magic link photo sharing)
// ── Portal Ideabox (spec 20260817-portal-ideabox §7) ────────────────────────
//
// ⚠ NOT `createApiClient`. CLAUDE.md states that both apps use
// `createApiClient()` from @caro/shared/api — THE PORTAL DOES NOT. It has its own
// `axiosInstance` above (:15) exporting ~30 domain objects, and its only shared
// import is `API_ENDPOINTS` (:3). `createApiClient` is resident-app only. Building
// this "shared-first per CLAUDE.md" would produce a THIRD API layer in the portal.
//
// Modelled on `estimatesApi` (:852) — every path derives from API_ENDPOINTS via
// stripApiPrefix() — not on the legacy hand-typed `ticketsApi` (:325).
// Customer onboarding (spec 20260819-registrationandonboarding/spec-01-onboarding.md §8).
// Paths come ONLY from the shared API_ENDPOINTS.
//
// The three CARO-side calls sit behind PROTECTED permissions (onboarding.invite / .view /
// .approve), so a 403 here is the expected answer for anyone who has not been granted them
// explicitly — including an `admin`. See specs/.../PERMISSIONS-GRANT.md before filing it
// as a bug.
export const onboardingApi = {
  // POST /api/onboarding/invites — mints a single-use 30-day link and mails it.
  // The response carries the RAW link once, so the operator can copy it if the mail bounced.
  createInvite: (data) =>
    axiosInstance.post(stripApiPrefix(API_ENDPOINTS.ONBOARDING_INVITES), data),
  listInvites: () => axiosInstance.get(stripApiPrefix(API_ENDPOINTS.ONBOARDING_INVITES)),
  // Revokes rather than deletes: who was invited, by whom, and that it was withdrawn is
  // exactly the history an audit needs.
  revokeInvite: (id) =>
    axiosInstance.delete(stripApiPrefix(API_ENDPOINTS.ONBOARDING_INVITE_DELETE(id))),

  // Applications queue (gate 2). Cross-tenant — CARO only, behind onboarding.view.
  listApplications: (params = {}) =>
    axiosInstance.get(stripApiPrefix(API_ENDPOINTS.ONBOARDING_APPLICATIONS), { params }),
  getApplication: (id) =>
    axiosInstance.get(stripApiPrefix(API_ENDPOINTS.ONBOARDING_APPLICATION(id))),
  // Creates the company + first company_admin and mails the activation link.
  // Does NOT enable paid modules — that stays a separate act behind modules.manage.
  approve: (id, data) =>
    axiosInstance.post(stripApiPrefix(API_ENDPOINTS.ONBOARDING_APPROVE(id)), data),
  reject: (id, reason) =>
    axiosInstance.post(stripApiPrefix(API_ENDPOINTS.ONBOARDING_REJECT(id)), { reason }),
};

// PUBLIC onboarding calls — no bearer token involved. The invite / status / activation
// token IS the authorisation, so these are deliberately kept separate from onboardingApi
// above, which is the CARO-side surface.
// Self-service password recovery. PUBLIC — the reset token is the authorisation.
//
// The portal had no reset screen at all until the password sign-in form was added; before
// that every account was Entra and Microsoft owned the recovery. A customer onboarded with
// email+password who mistypes at activation would otherwise be locked out permanently.
export const passwordResetApi = {
  // Always answers success, whether or not the address exists — it must not confirm which
  // e-mails have accounts.
  request: (email) => axiosInstance.post('/password-reset/request', { email }),
  validate: (token) => axiosInstance.get('/password-reset/validate', { params: { token } }),
  reset: (token, newPassword) =>
    axiosInstance.post('/password-reset/reset', { token, newPassword }),
};

export const onboardingPublicApi = {
  validateInvite: (token) =>
    axiosInstance.get(stripApiPrefix(API_ENDPOINTS.ONBOARDING_INVITE_VALIDATE(token))),
  submit: (data) =>
    axiosInstance.post(stripApiPrefix(API_ENDPOINTS.ONBOARDING_APPLICATIONS), data),
  status: (token) =>
    axiosInstance.get(stripApiPrefix(API_ENDPOINTS.ONBOARDING_STATUS(token))),
  validateActivation: (token) =>
    axiosInstance.get(stripApiPrefix(API_ENDPOINTS.ONBOARDING_ACTIVATE(token))),
  activate: (token, password) =>
    axiosInstance.post(stripApiPrefix(API_ENDPOINTS.ONBOARDING_ACTIVATE(token)), { password }),
};

// The customer's own organisation page (spec-02 D18). Backed by /api/company/settings,
// which is permission-gated per company rather than role-gated — that is precisely why
// /company-management never worked for a company_admin and this does.
//
// The company is resolved SERVER-SIDE from the caller's own user. There is deliberately no
// companyId parameter here; adding one would recreate the cross-tenant surface this page
// exists to avoid.
export const organisationApi = {
  get: () => axiosInstance.get('/company/settings'),
  update: (data) => axiosInstance.put('/company/settings', data),
};

// /unit-audit: the organisation-wide "every apartment must contain" profile
// (companies.settings.unitAuditProfile). `companyId` is honoured for platform admins
// only — the API resolves everyone else to their own organisation whatever is passed
// (utils/tenantScope.effectiveCompanyId), so passing it is safe from any role.
export const unitAuditApi = {
  getProfile: (companyId) =>
    axiosInstance.get('/unit-audit/profile', { params: companyId ? { companyId } : {} }),
  updateProfile: (companyId, rules) =>
    axiosInstance.put('/unit-audit/profile', { rules }, { params: companyId ? { companyId } : {} }),
};

// /database-health: the database SERVER, not a tenant's slice of it. Read on
// system.db_health.view, the two actions on system.db_health.manage (both PROTECTED).
// Both actions answer 202 and run in the background; poll getOverview() for the outcome.
export const dbHealthApi = {
  getOverview: () => axiosInstance.get('/system/db-health'),
  runJob: (name) => axiosInstance.post(`/system/db-health/jobs/${encodeURIComponent(name)}/run`),
  optimizeTable: (table, acknowledgeUnknownDisk = false) =>
    axiosInstance.post(`/system/db-health/tables/${encodeURIComponent(table)}/optimize`, {
      acknowledgeUnknownDisk,
    }),
};

/**
 * Audit trail (NIS2). Gated on `audit.view`; the export on `audit.export`.
 *
 * The export is fetched as a BLOB through the same axios instance rather than opened
 * as a URL. A plain `window.open` navigation carries no Authorization header, so the
 * endpoint would answer 401 and the browser would download the error page as a .csv —
 * a file that looks like an export and contains nothing.
 */
export const auditApi = {
  list: (params = {}) => axiosInstance.get('/audit', { params }),
  summary: (params = {}) => axiosInstance.get('/audit/summary', { params }),
  actions: (params = {}) => axiosInstance.get('/audit/actions', { params }),
  exportCsv: (params = {}) =>
    axiosInstance.get('/audit/export', { params, responseType: 'blob' }),
};

export const ideasApi = {
  // GET /api/ideas — the board. Filters: { status, theme, kind, search, sort, horizon,
  // mine, limit }. Empty values and 'all' are dropped so the query stays clean.
  // `kind` = 'functional' | 'ui' (20260925-ui-feedback); the server ignores anything else.
  list: (params = {}) => {
    const query = {};
    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return;
      if (value === 'all') return;
      query[key] = value;
    });
    return axiosInstance.get(stripApiPrefix(API_ENDPOINTS.IDEAS_LIST), { params: query });
  },
  // POST /api/ideas — submit. Rate-limited server-side (5/user/day, 20/company/day).
  // The body goes through unchanged: { title, body, … } for a functional idea, or
  // { kind: 'ui', body, uiContext } from the UI-feedback widget (20260925-ui-feedback).
  create: (data) => axiosInstance.post(stripApiPrefix(API_ENDPOINTS.IDEAS_LIST), data),
  // GET /api/ideas/:ref — detail, including the latest draft and its open questions.
  getOne: (ref) => axiosInstance.get(stripApiPrefix(API_ENDPOINTS.IDEA(ref))),

  // POST /api/ideas/:ref/triage — publish | decline | merge. CARO only.
  // ⚠ The server REJECTS with 422 unless `piiConfirmed === true`. The disabled
  // buttons in TriageTab are a courtesy; this 422 is the actual control.
  triage: (ref, data) => axiosInstance.post(stripApiPrefix(API_ENDPOINTS.IDEA_TRIAGE(ref)), data),
  // POST /api/ideas/:ref/triage { action: 'reclassify', kind } — change ui ↔ functional
  // while the idea is `submitted` (20260925-ui-feedback, V-10). Deliberately WITHOUT
  // piiConfirmed: relabelling publishes nothing, and the server handles it before the PII
  // gate. 409 outside `submitted`.
  reclassify: (ref, kind) =>
    axiosInstance.post(stripApiPrefix(API_ENDPOINTS.IDEA_TRIAGE(ref)), { action: 'reclassify', kind }),
  // POST /api/ideas/:ref/queue — "Send to AI now". Behind the SAME PII gate when
  // the idea is still `submitted`.
  queue: (ref, data = {}) => axiosInstance.post(stripApiPrefix(API_ENDPOINTS.IDEA_QUEUE(ref)), data),
  // POST /api/ideas/:ref/roadmap — { horizon: 'now'|'next'|'later', note }.
  roadmap: (ref, data) => axiosInstance.post(stripApiPrefix(API_ENDPOINTS.IDEA_ROADMAP(ref)), data),
  // POST /api/ideas/:ref/ship — the one transition a human owns (AC-12).
  ship: (ref, data = {}) => axiosInstance.post(stripApiPrefix(API_ENDPOINTS.IDEA_SHIP(ref)), data),
  // POST /api/ideas/:ref/questions/:oq — answering is what makes attempt #2 better
  // than #1 rather than an identical re-roll.
  answerQuestion: (ref, oq, data) =>
    axiosInstance.post(stripApiPrefix(API_ENDPOINTS.IDEA_QUESTION(ref, oq)), data),
  // POST /api/ideas/:ref/regenerate — re-queue after answering. Never automatic.
  regenerate: (ref) => axiosInstance.post(`${stripApiPrefix(API_ENDPOINTS.IDEA(ref))}/regenerate`),

  // GET /api/ideas/agent/health — feeds the board's `Agent ✔ 12 min ago` chip.
  // Load-bearing, not chrome: without it a dead systemd timer is visually identical
  // to an empty queue (AC-13).
  agentHealth: () => axiosInstance.get(stripApiPrefix(API_ENDPOINTS.IDEAS_AGENT_HEALTH)),

  // Phase 2 (voting). Declared here so no path is ever hand-typed later; no UI
  // calls these yet — Phase 1 deliberately ships with no vote control.
  vote: (ref) => axiosInstance.post(stripApiPrefix(API_ENDPOINTS.IDEA_VOTE(ref))),
  withdrawVote: (ref) => axiosInstance.delete(stripApiPrefix(API_ENDPOINTS.IDEA_VOTE(ref))),
};

export const familyLinksApi = {
  getAll: (params) => axiosInstance.get('/family-links', { params }),
  create: (data) => axiosInstance.post('/family-links', data),
  deactivate: (id) => axiosInstance.delete(`/family-links/${id}`),
  reactivate: (id) => axiosInstance.post(`/family-links/${id}/reactivate`),
  getPhotos: (linkId, params) => axiosInstance.get(`/family-links/${linkId}/photos`, { params }),
};

// Agenda (BASE tier) — spec 20260825-animatie-activiteiten-en-agenda §7.1.
//
// Paths come from the shared API_ENDPOINTS, the single source of truth. This is a
// BASE surface: no module gate, only `agenda.view` / `agenda.manage` server-side.
//
// ⚠ The list returns one row per OCCURRENCE, not per entry — recurrence is
// expanded on read (LD-3), so a weekly series arrives as N rows sharing one `uuid`
// and differing in `startsAt`. Anything keying on `uuid` alone collapses a series.
//
// ⚠ Resident-PRIVATE entries are stripped SERVER-side for everyone who is neither
// the owner nor CARO staff (LD-9). There is no parameter here that asks for them,
// deliberately: no permission key grants them either.
export const agendaApi = {
  list: (params) => axiosInstance.get(stripApiPrefix(API_ENDPOINTS.AGENDA), { params }),
  create: (data) => axiosInstance.post(stripApiPrefix(API_ENDPOINTS.AGENDA), data),
  update: (uuid, data) =>
    axiosInstance.patch(stripApiPrefix(API_ENDPOINTS.AGENDA_ENTRY(uuid)), data),
  // ⚠ `params` is not optional decoration for a cross-organisation caller. A DELETE
  // carries no body, so `companyId` can only reach `effectiveCompanyId` through the
  // query string — without it a CARO admin deleting a customer's entry is judged
  // against CARO's own company and gets 404.
  remove: (uuid, params) =>
    axiosInstance.delete(stripApiPrefix(API_ENDPOINTS.AGENDA_ENTRY(uuid)), { params }),
};

// Activities (PAID module `activities`) — spec 20260825-animatie-activiteiten-en-agenda §7.2.
//
// Paths come from the shared API_ENDPOINTS. Every call is gated SERVER-side on BOTH the
// `activities` module and an `activities.*` permission; a 403 body carries `code` so the
// UI can tell MODULE_NOT_ACTIVE apart from a permission refusal. ModuleGate in the page
// is the affordance half — it hides buttons, it does not protect anything.
//
// ⚠ `materialise({ dryRun: true })` writes NOTHING and returns the would-be list. It is
// part of the feature, not a debug aid: LD-4 removes the background job on purpose, so
// the button shows exactly what it would create before it creates anything.
export const activitiesApi = {
  list: (params) => axiosInstance.get(stripApiPrefix(API_ENDPOINTS.ACTIVITIES), { params }),
  get: (uuid, params) => axiosInstance.get(stripApiPrefix(API_ENDPOINTS.ACTIVITY(uuid)), { params }),
  create: (data) => axiosInstance.post(stripApiPrefix(API_ENDPOINTS.ACTIVITIES), data),
  update: (uuid, data) => axiosInstance.patch(stripApiPrefix(API_ENDPOINTS.ACTIVITY(uuid)), data),
  archive: (uuid, data) =>
    axiosInstance.post(stripApiPrefix(API_ENDPOINTS.ACTIVITY_ARCHIVE(uuid)), data || {}),

  // DELETE only while nothing has happened to it: the server answers HAS_SIGNUPS when
  // the activity is part of the record, and archiving is then the only way out.
  remove: (uuid, params) =>
    axiosInstance.delete(stripApiPrefix(API_ENDPOINTS.ACTIVITY(uuid)), { params }),

  materialise: (uuid, body) =>
    axiosInstance.post(stripApiPrefix(API_ENDPOINTS.ACTIVITY_MATERIALISE(uuid)), body),

  listOccurrences: (uuid, params) =>
    axiosInstance.get(stripApiPrefix(API_ENDPOINTS.ACTIVITY_OCCURRENCES(uuid)), { params }),
  updateOccurrence: (uuid, data) =>
    axiosInstance.patch(stripApiPrefix(API_ENDPOINTS.ACTIVITY_OCCURRENCE(uuid)), data),
  cancelOccurrence: (uuid, data) =>
    axiosInstance.post(stripApiPrefix(API_ENDPOINTS.ACTIVITY_OCCURRENCE_CANCEL(uuid)), data || {}),

  // Per DATE, never per series.
  roster: (uuid, params) =>
    axiosInstance.get(stripApiPrefix(API_ENDPOINTS.ACTIVITY_OCCURRENCE_ROSTER(uuid)), { params }),
  createSignup: (occurrenceUuid, data) =>
    axiosInstance.post(
      stripApiPrefix(API_ENDPOINTS.ACTIVITY_OCCURRENCE_SIGNUPS(occurrenceUuid)),
      data,
    ),
  updateSignup: (uuid, data) =>
    axiosInstance.patch(stripApiPrefix(API_ENDPOINTS.ACTIVITY_SIGNUP(uuid)), data),

  // `params` carries companyId for a cross-organisation caller — same reason as
  // agendaApi.remove: without it the site is judged against the caller's OWN company.
  getSiteConfig: (siteId, params) =>
    axiosInstance.get(stripApiPrefix(API_ENDPOINTS.ACTIVITY_SITE_CONFIG(siteId)), { params }),
  putSiteConfig: (siteId, data) =>
    axiosInstance.put(stripApiPrefix(API_ENDPOINTS.ACTIVITY_SITE_CONFIG(siteId)), data),
};

// Energiemeting (PAID module `metering`) — specs/20261001-mbus-portal-integration (T095).
//
// Paths come ONLY from the shared API_ENDPOINTS, on the portal's one axiosInstance (no new
// instance, foundation I.4). Every route is gated SERVER-side: permission (403
// INSUFFICIENT_PERMISSIONS) → tenant (404) → entitlement (403 MODULE_NOT_ACTIVE /
// MODULE_NEVER_ACTIVE) → site (403 METERING_SITE_DISABLED / METERING_SITE_NEVER_ENABLED).
// Error bodies are `{ success:false, error, code, ... }`; pages/EnergyMetering/meteringErrors.js
// turns them into Dutch sentences.
//
// The site is resolved from the route by the API (the site owns the tenant), so no
// `companyId` is sent: an unknown query/body field is refused on the channel routes.
const METERING_TEST_TIMEOUT_MS = 40_000; // the API itself waits up to 30 s for the hub
export const meteringApi = {
  // GET → MeteringSiteConfig. Works on a never-enabled site (the switch must be visible).
  getSiteConfig: (siteId) =>
    axiosInstance.get(stripApiPrefix(API_ENDPOINTS.METERING_SITE_CONFIG(siteId))),
  // PUT { enabled } → config + { changed, sync, uninstall? }. Switching OFF uninstalls the
  // hub service (without --purge); the data stays.
  setSiteEnabled: (siteId, enabled) =>
    axiosInstance.put(stripApiPrefix(API_ENDPOINTS.METERING_SITE_CONFIG(siteId)), { enabled }),

  listChannels: (siteId) =>
    axiosInstance.get(stripApiPrefix(API_ENDPOINTS.METERING_SITE_CHANNELS(siteId))),
  // body: MeteringChannelInput. `secret` ABSENT = unchanged, null = clear, { password } = set.
  createChannel: (siteId, body) =>
    axiosInstance.post(stripApiPrefix(API_ENDPOINTS.METERING_SITE_CHANNELS(siteId)), body),
  // Partial bodies are fine (e.g. { enabled } for the on/off switch).
  updateChannel: (id, body) =>
    axiosInstance.put(stripApiPrefix(API_ENDPOINTS.METERING_CHANNEL(id)), body),
  // Soft delete: meters and their history stay.
  deleteChannel: (id) =>
    axiosInstance.delete(stripApiPrefix(API_ENDPOINTS.METERING_CHANNEL(id))),
  // → { requestId, ok, durationMs, details, error } or { ok:false, reason:'NO_RESPONSE' }.
  testChannel: (id) =>
    axiosInstance.post(
      stripApiPrefix(API_ENDPOINTS.METERING_CHANNEL_TEST(id)),
      {},
      { timeout: METERING_TEST_TIMEOUT_MS },
    ),
  listProfiles: () => axiosInstance.get(stripApiPrefix(API_ENDPOINTS.METERING_PROFILES)),
  // forcePoll / getConfig (view), forceSnapshot / syncConfig (manage). 429 RATE_LIMITED
  // { retryAfterSec } — one command per hub per 10 s.
  hubCommand: (deviceId, command) =>
    axiosInstance.post(stripApiPrefix(API_ENDPOINTS.METERING_HUB_COMMAND(deviceId, command)), {}),

  // params: { status, channelId, buildingId, linked, page, limit } → data[] + meta.
  listMeters: (siteId, params) =>
    axiosInstance.get(stripApiPrefix(API_ENDPOINTS.METERING_SITE_METERS(siteId)), { params }),
  getMeter: (id) => axiosInstance.get(stripApiPrefix(API_ENDPOINTS.METERING_METER(id))),
  // body: { participatesInDistribution?, status?: 'retired'|'active' }
  patchMeter: (id, body) =>
    axiosInstance.patch(stripApiPrefix(API_ENDPOINTS.METERING_METER(id)), body),
  // params: { from, to } ISO instants; > 400 days → 422 RANGE_TOO_LARGE.
  getReadings: (id, params) =>
    axiosInstance.get(stripApiPrefix(API_ENDPOINTS.METERING_METER_READINGS(id)), { params }),
  // params: { from, to } YYYY-MM-DD, both inclusive.
  getSnapshots: (id, params) =>
    axiosInstance.get(stripApiPrefix(API_ENDPOINTS.METERING_METER_SNAPSHOTS(id)), { params }),

  // params: { from?, to? } YYYY-MM-DD. A never-enabled site answers { moduleEnabled:false }.
  locationMeters: (locationId, params) =>
    axiosInstance.get(stripApiPrefix(API_ENDPOINTS.METERING_LOCATION_METERS(locationId)), {
      params,
    }),
  // params: { from, to, medium? } YYYY-MM-DD, `to` exclusive. Audited server-side.
  locationConsumption: (locationId, params) =>
    axiosInstance.get(stripApiPrefix(API_ENDPOINTS.METERING_LOCATION_CONSUMPTION(locationId)), {
      params,
    }),
  // body: { validFrom?, validTo?, startReading?, endReading?, note? }; 409 ASSIGNMENT_OVERLAP.
  patchAssignment: (id, body) =>
    axiosInstance.patch(stripApiPrefix(API_ENDPOINTS.METERING_ASSIGNMENT(id)), body),

  // params: { state: 'open'|'closed'|'all', code, page, limit } → data[] + meta.
  listAlarms: (siteId, params) =>
    axiosInstance.get(stripApiPrefix(API_ENDPOINTS.METERING_SITE_ALARMS(siteId)), { params }),
  ackAlarm: (id) => axiosInstance.post(stripApiPrefix(API_ENDPOINTS.METERING_ALARM_ACK(id)), {}),

  // body: { from, to, format: 'csv'|'xlsx'|'pdf' } → file. A refusal arrives as a Blob too;
  // meteringErrors.readBlobError() turns it back into `{ code, error }`.
  exportSite: (siteId, body) =>
    axiosInstance.post(stripApiPrefix(API_ENDPOINTS.METERING_SITE_EXPORTS(siteId)), body, {
      responseType: 'blob',
    }),
};

// residentEventsApi is GONE (Phase 4 of 20260825-animatie-activiteiten-en-agenda,
// T-60). It fronted /api/resident-events, which carried authMiddleware and nothing
// else: any authenticated user of any customer read and wrote any other customer's
// agenda by changing one integer. Its only consumer was LocationEventsTab, deleted in
// the same change-set. Use `agendaApi` above.

export const einkDisplayApi = {
  // Template management (portal designer — keyed by device type ID)
  getAll: () => axiosInstance.get('/einkDisplays/templates'),
  getByDeviceType: (deviceTypeId) => axiosInstance.get(`/einkDisplays/templates/${deviceTypeId}`),
  saveTemplate: (deviceTypeId, data) => axiosInstance.post(`/einkDisplays/templates/${deviceTypeId}`, data),
  deleteTemplate: (deviceTypeId) => axiosInstance.delete(`/einkDisplays/templates/${deviceTypeId}`),
  push: (deviceTypeId) => axiosInstance.post(`/einkDisplays/templates/${deviceTypeId}/push`),
  // Per-device variable monitoring (portal — keyed by device type or device ID)
  getDeviceVariables: (deviceTypeId) => axiosInstance.get(`/einkDisplays/devices/${deviceTypeId}/variables`),
  getDeviceVars: (deviceId) => axiosInstance.get(`/einkDisplays/${deviceId}/variables`),
  // Device polling (kept for device scripts — keyed by device ID)
  getState: (deviceId, since, varSince) => axiosInstance.get(`/einkDisplays/${deviceId}/state`, { params: { since, varSince } }),
  updateVariables: (deviceId, variables) => axiosInstance.post(`/einkDisplays/${deviceId}/variables`, variables),
  sendButton: (deviceId, button) => axiosInstance.post(`/einkDisplays/${deviceId}/button`, { button }),
};

// Export the axios instance for direct use by other services
export { axiosInstance, publicAxiosInstance };

// Export default API client
const apiClient = {
  auth: authApi,
  tickets: ticketsApi,
  locations: locationsApi,
  devices: devicesApi,
  notifications: notificationsApi,
  users: usersApi,
  logs: logsApi,
  cameraFootageRequests: cameraFootageRequestsApi,
  companies: companiesApi,
  fieldInterventions: fieldInterventionsApi,
  documentation: documentationApi,
  knowledgeBase: knowledgeBaseApi,
  files: filesApi,
  folders: foldersApi,
  documents: documentsApi,
  hexnode: hexnodeApi,
  unifi: unifiApi,
  clearvox: clearvoxApi,
  baseIP: baseIPApi,
  persons: personsApi,
  personTypes: personTypesApi,
  residencies: residenciesApi,
  firmwareVersions: firmwareVersionsApi,
  updatePaths: updatePathsApi,
  emergencyServices: emergencyServicesApi,
  alarms: alarmsApi,
  alarmStates: alarmStatesApi,
  alarmTypes: alarmTypesApi,
  buttonTests: buttonTestsApi,
  services: servicesApi,
  deviceTypes: deviceTypesApi,
  qbus: qbusApi,
  telephony: telephonyApi,
  tabletPermissions: tabletPermissionsApi,
  familyLinks: familyLinksApi,
  agenda: agendaApi,
  einkDisplay: einkDisplayApi,
  siteBoard: siteBoardApi,
  siteBoardAdmin: siteBoardAdminApi,
  ideas: ideasApi,
  onboarding: onboardingApi,
  onboardingPublic: onboardingPublicApi,
  passwordReset: passwordResetApi,
  organisation: organisationApi,
  audit: auditApi,
  notificationSounds: notificationSoundsApi,
  notificationImages: notificationImagesApi,
};

export default apiClient;

// Export twonApi separately since it's in its own service file
export { twonApi } from './twonService';